import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/toaster';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Componentes extraídos para arquivos separados
import CotacaoTab from '@/pages/CotacaoTab';
import AdministrativoTab from '@/pages/AdministrativoTab';
import GestaoPedidosTab from '@/pages/GestaoPedidosTab';
import PromocaoTab from '@/pages/PromocaoTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.defaults.baseURL = API;

// Função utilitária para buscar todos os registros com paginação automática
const fetchAllPaginated = async (endpoint, limit = 1000) => {
  let allItems = [];
  let skip = 0;
  let hasMore = true;
  
  while (hasMore) {
    const response = await axios.get(`${endpoint}?skip=${skip}&limit=${limit}`);
    const data = response.data;
    
    // Suporta resposta paginada ou array direto
    if (data.items) {
      allItems = [...allItems, ...data.items];
      hasMore = data.has_more || false;
      skip += limit;
    } else if (Array.isArray(data)) {
      allItems = data;
      hasMore = false;
    } else {
      allItems = [];
      hasMore = false;
    }
  }
  
  return allItems;
};

// Axios interceptors for authentication
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Remove response interceptor temporarily to debug
// axios.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// Custom Confirmation Dialog Component
const ConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  onCancel,
  title = "Confirmar Exclusão", 
  message = "Tem certeza que deseja excluir este item?",
  confirmText = "Sim",
  cancelText = "Cancelar",
  confirmVariant = "destructive"
}) => {
  const handleClose = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };
  
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span className="text-red-600">⚠️</span>
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription className="py-4">
            {message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex space-x-2 sm:space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
          >
            {cancelText}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            className="flex-1"
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Login Component
const Login = ({ onLogin }) => {
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/login', { login, senha });
      const { access_token, usuario } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('usuario', JSON.stringify(usuario));
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast({
        title: "Login realizado com sucesso!",
        description: `Bem-vindo, ${usuario.nome_completo || usuario.nome || 'Usuário'}!`
      });
      
      onLogin(usuario);
    } catch (error) {
      toast({
        title: "Erro no login",
        description: error.response?.data?.detail || "Login ou senha incorretos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          {/* Logo Facilita Ceasa */}
          <div className="flex justify-center mb-8">
            <img 
              src="/logo-facilita-ceasa.png" 
              alt="Facilita Ceasa" 
              className="h-64 w-auto object-contain"
            />
          </div>
          <p className="text-gray-600 text-lg">Sistema de Gestão de Cotações</p>
        </div>
        
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login
            </label>
            <input
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
              placeholder="Digite seu login"
              required
              data-testid="login-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
              placeholder="Digite sua senha"
              required
              data-testid="password-input"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-700 text-white py-3 px-4 rounded-lg hover:bg-green-800 focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            data-testid="login-button"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ usuario, onLogout }) => {
  const [activeTab, setActiveTab] = useState('produtos');
  const [produtos, setProdutos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [cotacoes, setCotacoes] = useState([]);
  const [separacoes, setSeparacoes] = useState([]);
  const [recebimentos, setRecebimentos] = useState([]);
  const [bonificacoes, setBonificacoes] = useState([]);
  const [notasAdministrativas, setNotasAdministrativas] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [promocoes, setPromocoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Confirmation Dialog State
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Sim',
    cancelText: 'Cancelar'
  });

  // Function to show confirmation dialog (returns Promise for async/await)
  const showConfirmDialog = (title, message) => {
    return new Promise((resolve) => {
      setConfirmationDialog({
        isOpen: true,
        title: title || "Confirmação",
        message: message || "Confirma esta ação?",
        onConfirm: () => {
          resolve(true);
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        },
        onCancel: () => {
          resolve(false);
          setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
        },
        confirmText: 'Sim',
        cancelText: 'Cancelar'
      });
    });
  };

  const closeConfirmDialog = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      isOpen: false,
      onConfirm: null
    });
  };

  // Check access permissions
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const hasAccess = (tabName) => {
    const permissions = {
      'produtos': ['Comprador', 'Fornecedor'],
      'pedidos': ['Gerente', 'Comprador'],
      'cotacao': ['Fornecedor', 'Comprador', 'Administrador'],
      'usuarios': ['Comprador'],
      'separacao': ['Motorista', 'Comprador'],
      'recebimento': ['Comprador', 'Gerente', 'Administrador'],
      'bonificacao': ['Comprador', 'Gerente', 'Motorista'],
      'administrativo': ['Administrador', 'Comprador'],
      'gestaopedidos': ['Comprador', 'Fornecedor', 'Administrador'],
      'promocao': ['Comprador', 'Administrador', 'Gerente']
    };
    return permissions[tabName]?.includes(usuario.tipo_usuario);
  };

  // Load data based on active tab
  useEffect(() => {
    loadTabData();
  }, [activeTab]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'produtos':
          if (hasAccess('produtos')) {
            const produtos = await fetchAllPaginated('/produtos');
            setProdutos(produtos);
          }
          break;
        case 'usuarios':
          if (hasAccess('usuarios')) {
            const response = await axios.get('/usuarios');
            setUsuarios(response.data);
          }
          break;
        case 'pedidos':
          if (hasAccess('pedidos')) {
            const [pedidosData, produtosData, cotacoesData, promocoesResponse] = await Promise.all([
              fetchAllPaginated('/pedidos'),
              fetchAllPaginated('/produtos'),
              fetchAllPaginated('/cotacoes'),
              axios.get('/promocoes').catch(() => ({ data: [] })) // Fallback se usuário não tiver permissão
            ]);
            setPedidos(pedidosData);
            setProdutos(produtosData);
            setCotacoes(cotacoesData);
            setPromocoes(promocoesResponse.data || []);
          }
          break;
        case 'cotacao':
          if (hasAccess('cotacao')) {
            const cotacoes = await fetchAllPaginated('/cotacoes');
            setCotacoes(cotacoes);
          }
          break;
        case 'separacao':
          if (hasAccess('separacao')) {
            const [separacoesResponse, pedidosData] = await Promise.all([
              axios.get('/separacoes'),
              fetchAllPaginated('/pedidos')
            ]);
            setSeparacoes(separacoesResponse.data);
            setPedidos(pedidosData);
          }
          break;
        case 'recebimento':
          if (hasAccess('recebimento')) {
            const [recebimentosResponse, pedidosData] = await Promise.all([
              axios.get('/recebimentos'),
              fetchAllPaginated('/pedidos')
            ]);
            setRecebimentos(recebimentosResponse.data || []);
            setPedidos(pedidosData);
          }
          break;
        case 'bonificacao':
          if (hasAccess('bonificacao')) {
            const [bonificacoesResponse, produtosData] = await Promise.all([
              axios.get('/bonificacoes'),
              fetchAllPaginated('/produtos')
            ]);
            setBonificacoes(bonificacoesResponse.data || []);
            setProdutos(produtosData);
          }
          break;
        case 'administrativo':
          if (hasAccess('administrativo')) {
            const [notasResponse, recebimentosResponse, cotacoesData, produtosData, pedidosData] = await Promise.all([
              axios.get('/notas-administrativas'),
              axios.get('/recebimentos'),
              fetchAllPaginated('/cotacoes'),
              fetchAllPaginated('/produtos'),
              fetchAllPaginated('/pedidos')
            ]);
            setNotasAdministrativas(notasResponse.data || []);
            setRecebimentos(recebimentosResponse.data || []);
            setCotacoes(cotacoesData);
            setProdutos(produtosData);
            setPedidos(pedidosData);
          }
          break;
        case 'gestaopedidos':
          if (hasAccess('gestaopedidos')) {
            if (temPermissaoComprador()) {
              // COMPRADOR precisa de todos os dados
              const [enviosResponse, pedidosData, cotacoesData, produtosData] = await Promise.all([
                axios.get('/envios'),
                fetchAllPaginated('/pedidos'),
                fetchAllPaginated('/cotacoes'),
                fetchAllPaginated('/produtos')
              ]);
              setEnvios(enviosResponse.data || []);
              setPedidos(pedidosData);
              setCotacoes(cotacoesData);
              setProdutos(produtosData);
            } else if (usuario.tipo_usuario === 'Fornecedor') {
              // FORNECEDOR precisa de envios, cotações, pedidos e produtos para a funcionalidade GESTÃO PEDIDOS
              const [enviosResponse, cotacoesData, pedidosData, produtosData] = await Promise.all([
                axios.get('/envios'),
                fetchAllPaginated('/cotacoes'),
                fetchAllPaginated('/pedidos'),
                fetchAllPaginated('/produtos')
              ]);
              setEnvios(enviosResponse.data || []);
              setCotacoes(cotacoesData);
              setPedidos(pedidosData);
              setProdutos(produtosData);
            }
          }
          break;
        case 'promocao':
          if (hasAccess('promocao')) {
            const [promocoesResponse, produtosData, cotacoesData] = await Promise.all([
              axios.get('/promocoes'),
              fetchAllPaginated('/produtos'),
              fetchAllPaginated('/cotacoes')
            ]);
            setPromocoes(promocoesResponse.data || []);
            setProdutos(produtosData);
            setCotacoes(cotacoesData);
          }
          break;
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    delete axios.defaults.headers.common['Authorization'];
    onLogout();
  };

  // Get available tabs based on user permissions
  const availableTabs = [
    { id: 'produtos', name: 'Produtos', icon: '📦' },
    { id: 'pedidos', name: 'Pedidos', icon: '📝' },
    { id: 'cotacao', name: 'Cotação', icon: '💲' },
    { id: 'usuarios', name: 'Gestão de Usuários', icon: '👥' },
    { id: 'separacao', name: 'Separação de Entrega', icon: '🚚' },
    { id: 'recebimento', name: 'Recebimento', icon: '✅' },
    { id: 'bonificacao', name: 'Bonificação', icon: '🎁' },
    { id: 'administrativo', name: 'Administrativo', icon: '📊' },
    { id: 'gestaopedidos', name: 'Gestão Pedidos', icon: '📋' },
    { id: 'promocao', name: 'Promoção', icon: '🎯' }
  ].filter(tab => hasAccess(tab.id));

  // Set default active tab to first available tab
  React.useEffect(() => {
    if (availableTabs.length > 0 && !hasAccess(activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Close sidebar when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && !event.target.closest('.sidebar') && !event.target.closest('.hamburger-menu')) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-green-100 relative z-50">
        <div className="px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20 lg:h-28">
            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Hamburger Menu */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hamburger-menu p-1 sm:p-2 rounded-lg hover:bg-green-50 transition-colors"
                data-testid="hamburger-menu"
              >
                <span className="text-xl sm:text-2xl text-green-700">☰</span>
              </button>
              {/* Logo Facilita Ceasa */}
              <img 
                src="/logo-facilita-ceasa.png" 
                alt="Facilita Ceasa" 
                className="h-10 sm:h-14 lg:h-20 w-auto object-contain"
              />
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* User Info - Hidden on small screens, shown on medium+ */}
              <div className="hidden md:flex items-center text-sm text-gray-700">
                <span className="font-medium">{usuario.nome_completo || usuario.nome || 'Usuário'}</span>
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                  {usuario.tipo_usuario}
                </span>
                {usuario.filial && (
                  <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                    {usuario.filial}
                  </span>
                )}
              </div>
              {/* User Info Mobile - Only name and type on small screens */}
              <div className="flex md:hidden flex-col items-end text-xs text-gray-700">
                <span className="font-medium truncate max-w-[120px]">{(usuario.nome_completo || usuario.nome || 'Usuário').split(' ')[0]}</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs mt-1">
                  {usuario.tipo_usuario}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="text-xs sm:text-sm text-red-600 hover:text-red-800 font-medium whitespace-nowrap"
                data-testid="logout-button"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <div className={`sidebar fixed top-0 left-0 h-full w-80 bg-white border-r border-green-100 shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-green-100 bg-green-50">
          <div className="flex items-center space-x-2">
            <img 
              src="/logo-facilita-ceasa.png" 
              alt="Facilita Ceasa" 
              className="h-14 w-auto object-contain"
            />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-green-100 transition-colors"
            data-testid="close-sidebar"
          >
            <span className="text-xl text-green-700">✕</span>
          </button>
        </div>
        
        {/* Navigation Items */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-2">
            {availableTabs.map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSidebarOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-green-700 text-white shadow-md transform scale-105'
                      : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span className="font-medium">{tab.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info in Sidebar */}
        <div className="mt-auto p-4 border-t border-green-100 bg-green-50">
          <div className="text-sm text-gray-600">
            <div className="font-medium text-gray-900">{usuario.nome_completo || usuario.nome || 'Usuário'}</div>
            <div className="flex items-center mt-1 space-x-2">
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                {usuario.tipo_usuario}
              </span>
              {usuario.filial && (
                <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs">
                  {usuario.filial}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-0">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow min-h-96">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
                <span className="text-2xl">
                  {availableTabs.find(tab => tab.id === activeTab)?.icon}
                </span>
                <span>
                  {availableTabs.find(tab => tab.id === activeTab)?.name || 'Dashboard'}
                </span>
              </h2>
            </div>
            
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-3 text-gray-600">Carregando...</span>
                </div>
              ) : (
                <TabContent 
                  activeTab={activeTab} 
                  data={{
                    produtos,
                    usuarios,
                    pedidos,
                    cotacoes,
                    separacoes,
                    recebimentos,
                    bonificacoes,
                    notasAdministrativas,
                    envios,
                    promocoes
                  }}
                  onRefresh={loadTabData}
                  usuario={usuario}
                  showConfirmDialog={showConfirmDialog}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={confirmationDialog.onCancel}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
        confirmVariant="destructive"
      />
    </div>
  );
};

// Tab Content Component
const TabContent = ({ activeTab, data, onRefresh, usuario, showConfirmDialog }) => {
  switch (activeTab) {
    case 'produtos':
      return <ProdutosTab produtos={data.produtos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'usuarios':
      return <UsuariosTab usuarios={data.usuarios} onRefresh={onRefresh} showConfirmDialog={showConfirmDialog} />;
    case 'pedidos':
      return <PedidosTab pedidos={data.pedidos} produtos={data.produtos} cotacoes={data.cotacoes} promocoes={data.promocoes} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'cotacao':
      return <CotacaoTab cotacoes={data.cotacoes} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'separacao':
      return <SeparacaoTab separacoes={data.separacoes} pedidos={data.pedidos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'recebimento':
      return <RecebimentoTab recebimentos={data.recebimentos} pedidos={data.pedidos} produtos={data.produtos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'bonificacao':
      return <BonificacaoTab bonificacoes={data.bonificacoes} produtos={data.produtos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'administrativo':
      return <AdministrativoTab notasAdministrativas={data.notasAdministrativas} recebimentos={data.recebimentos} cotacoes={data.cotacoes} produtos={data.produtos} pedidos={data.pedidos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'gestaopedidos':
      return <GestaoPedidosTab envios={data.envios} pedidos={data.pedidos} cotacoes={data.cotacoes} produtos={data.produtos} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    case 'promocao':
      return <PromocaoTab promocoes={data.promocoes} produtos={data.produtos} cotacoes={data.cotacoes} onRefresh={onRefresh} usuario={usuario} showConfirmDialog={showConfirmDialog} />;
    default:
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🚧</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Funcionalidade em Desenvolvimento
          </h3>
          <p className="text-gray-600">
            Esta seção estará disponível em breve.
          </p>
        </div>
      );
  }
};

// Produtos Tab Component

// Componente especial para Fornecedores visualizarem e editarem seus produtos
const ProdutosFornecedorView = ({ produtos, usuario, onRefresh }) => {
  const [produtosComPrecos, setProdutosComPrecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [debounceTimers, setDebounceTimers] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  
  const toast = ({ title, description, variant }) => {
    if (variant === 'destructive') {
      alert(`ERRO: ${title}\n${description || ''}`);
    }
    // Não mostrar toast de sucesso para evitar interrupções
  };
  
  // Carregar produtos vinculados com preços
  const loadProdutosComPrecos = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/fornecedor/produtos-precos');
      setProdutosComPrecos(response.data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast({
        title: "Erro ao carregar produtos",
        description: error.response?.data?.detail || "Erro ao buscar dados",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadProdutosComPrecos();
  }, []);
  
  // Toggle expanded state for products with variations
  const toggleExpanded = (produtoId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [produtoId]: !prev[produtoId]
    }));
  };
  
  // Atualizar quantidade ou preço com debounce (produto sem variações)
  const handleUpdateField = (produtoId, field, value) => {
    // Atualizar estado local imediatamente para feedback visual
    setProdutosComPrecos(prev => prev.map(p => {
      if (p.id === produtoId) {
        const updated = { ...p, [field]: value ? parseFloat(value) : null };
        
        // Recalcular preco_kg_un
        if (p.unidade === 'UN' && field === 'preco_cx') {
          updated.preco_kg_un = value ? parseFloat(value) : null;
        } else if (updated.preco_cx && updated.quantidade_cx && updated.quantidade_cx > 0) {
          updated.preco_kg_un = updated.preco_cx / updated.quantidade_cx;
        }
        
        return updated;
      }
      return p;
    }));
    
    // Cancelar timer anterior se existir
    const timerKey = `${produtoId}-${field}`;
    if (debounceTimers[timerKey]) {
      clearTimeout(debounceTimers[timerKey]);
    }
    
    // Criar novo timer para salvar após 1 segundo de inatividade
    const newTimer = setTimeout(async () => {
      try {
        const produto = produtosComPrecos.find(p => p.id === produtoId);
        const updatedData = {
          quantidade_cx: field === 'quantidade_cx' ? (value ? parseFloat(value) : null) : produto.quantidade_cx,
          preco_cx: field === 'preco_cx' ? (value ? parseFloat(value) : null) : produto.preco_cx,
          precos_variacoes: produto.precos_variacoes || {}
        };
        
        await axios.put(`/fornecedor/produtos-precos/${produtoId}`, updatedData);
        
        // Salvo com sucesso - não mostrar mensagem
      } catch (error) {
        console.error('Erro ao atualizar:', error);
        toast({
          title: "Erro ao salvar",
          description: error.response?.data?.detail || "Erro ao salvar alterações",
          variant: "destructive"
        });
        
        // Recarregar dados em caso de erro
        loadProdutosComPrecos();
      } finally {
        // Remover timer do estado
        setDebounceTimers(prev => {
          const newTimers = { ...prev };
          delete newTimers[timerKey];
          return newTimers;
        });
      }
    }, 1000); // 1 segundo de debounce
    
    // Armazenar timer
    setDebounceTimers(prev => ({
      ...prev,
      [timerKey]: newTimer
    }));
  };
  
  // Atualizar preço de variação específica
  const handleUpdateVariacaoPreco = (produtoId, variacao, value) => {
    // Atualizar estado local imediatamente
    setProdutosComPrecos(prev => prev.map(p => {
      if (p.id === produtoId) {
        const precosVariacoes = { ...(p.precos_variacoes || {}) };
        precosVariacoes[variacao] = value ? parseFloat(value) : null;
        return { ...p, precos_variacoes: precosVariacoes };
      }
      return p;
    }));
    
    // Cancelar timer anterior
    const timerKey = `${produtoId}-variacao-${variacao}`;
    if (debounceTimers[timerKey]) {
      clearTimeout(debounceTimers[timerKey]);
    }
    
    // Criar novo timer para salvar
    const newTimer = setTimeout(async () => {
      try {
        const produto = produtosComPrecos.find(p => p.id === produtoId);
        const precosVariacoes = { ...(produto.precos_variacoes || {}) };
        precosVariacoes[variacao] = value ? parseFloat(value) : null;
        
        const updatedData = {
          quantidade_cx: produto.quantidade_cx,
          preco_cx: produto.preco_cx,
          precos_variacoes: precosVariacoes
        };
        
        await axios.put(`/fornecedor/produtos-precos/${produtoId}`, updatedData);
      } catch (error) {
        console.error('Erro ao atualizar preço da variação:', error);
        toast({
          title: "Erro ao salvar",
          description: error.response?.data?.detail || "Erro ao salvar preço da variação",
          variant: "destructive"
        });
        loadProdutosComPrecos();
      } finally {
        setDebounceTimers(prev => {
          const newTimers = { ...prev };
          delete newTimers[timerKey];
          return newTimers;
        });
      }
    }, 1000);
    
    setDebounceTimers(prev => ({
      ...prev,
      [timerKey]: newTimer
    }));
  };
  
  // Adicionar novo produto
  const handleAddProduto = async (produtoId) => {
    try {
      await axios.post(`/fornecedor/adicionar-produto/${produtoId}`);
      toast({ title: "Produto adicionado!" });
      setShowAddModal(false);
      loadProdutosComPrecos();
    } catch (error) {
      toast({
        title: "Erro ao adicionar produto",
        description: error.response?.data?.detail || "Erro",
        variant: "destructive"
      });
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Carregando produtos...</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Meus Produtos</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + ADICIONAR PRODUTO
        </button>
      </div>
      
      {/* Info sobre variações */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <strong>Dica:</strong> Para produtos com variações, clique no produto para expandir e definir preços individuais por variação. O peso (Quantidade CX) é compartilhado entre todas as variações.
      </div>
      
      {/* Tabela de produtos */}
      {produtosComPrecos.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Nenhum produto vinculado. Clique em "ADICIONAR PRODUTO" para começar.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantidade CX (kg)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço CX (R$)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preço KG/UN (R$)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {produtosComPrecos.map(produto => {
                const temVariacoes = produto.variacoes && produto.variacoes.length > 0;
                const isExpanded = expandedProducts[produto.id];
                
                return (
                  <React.Fragment key={produto.id}>
                    {/* Linha principal do produto */}
                    <tr 
                      className={`hover:bg-gray-50 ${temVariacoes ? 'cursor-pointer' : ''}`}
                      onClick={() => temVariacoes && toggleExpanded(produto.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {temVariacoes && (
                            <span className="text-gray-400">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-gray-900">{produto.nome}</div>
                            {temVariacoes && (
                              <div className="text-xs text-purple-600">
                                {produto.variacoes.length} variações
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {produto.unidade}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {produto.unidade === 'UN' ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            value={produto.quantidade_cx || ''}
                            onChange={(e) => handleUpdateField(produto.id, 'quantidade_cx', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="0.00"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {temVariacoes ? (
                          <span className="text-gray-400 text-sm italic">Ver variações abaixo</span>
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            value={produto.preco_cx || ''}
                            onChange={(e) => handleUpdateField(produto.id, 'preco_cx', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="0.00"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {temVariacoes ? (
                          <span className="text-gray-400 text-sm italic">-</span>
                        ) : (
                          <span className="text-gray-700 font-medium">
                            {produto.preco_kg_un ? `R$ ${produto.preco_kg_un.toFixed(2)}` : '-'}
                          </span>
                        )}
                      </td>
                    </tr>
                    
                    {/* Linhas de variações (quando expandido) */}
                    {temVariacoes && isExpanded && produto.variacoes.map(variacao => {
                      const precoVariacao = produto.precos_variacoes?.[variacao] || null;
                      const precoKgVariacao = precoVariacao && produto.quantidade_cx && produto.quantidade_cx > 0
                        ? precoVariacao / produto.quantidade_cx
                        : null;
                      
                      return (
                        <tr key={`${produto.id}-${variacao}`} className="bg-purple-50 hover:bg-purple-100">
                          <td className="px-6 py-3 whitespace-nowrap pl-12">
                            <div className="flex items-center gap-2">
                              <span className="text-purple-400">└</span>
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                {variacao}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className="text-gray-400 text-xs">-</span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className="text-gray-500 text-sm">
                              {produto.quantidade_cx ? `${produto.quantidade_cx} kg` : '-'}
                            </span>
                            <span className="text-xs text-gray-400 ml-1">(compartilhado)</span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              step="0.01"
                              value={precoVariacao || ''}
                              onChange={(e) => handleUpdateVariacaoPreco(produto.id, variacao, e.target.value)}
                              className="w-24 px-2 py-1 border border-purple-300 rounded text-sm bg-white"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className="text-purple-700 font-medium">
                              {precoKgVariacao ? `R$ ${precoKgVariacao.toFixed(2)}` : '-'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Modal Adicionar Produto */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Adicionar Produto</h3>
            
            <div className="space-y-2">
              {produtos
                .filter(p => !produtosComPrecos.some(pc => pc.id === p.id))
                .map(produto => (
                  <div
                    key={produto.id}
                    className="flex justify-between items-center p-3 border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <div>
                      <div className="font-medium">{produto.nome}</div>
                      <div className="text-sm text-gray-500">
                        {produto.unidade}
                        {produto.variacoes && produto.variacoes.length > 0 && (
                          <span className="ml-2 text-purple-600">
                            ({produto.variacoes.length} variações)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddProduto(produto.id)}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      Adicionar
                    </button>
                  </div>
                ))}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const ProdutosTab = ({ produtos, onRefresh, usuario, showConfirmDialog }) => {
  // Se for fornecedor, renderizar view diferente
  if (usuario?.tipo_usuario === 'Fornecedor') {
    return <ProdutosFornecedorView produtos={produtos} usuario={usuario} onRefresh={onRefresh} />;
  }
  
  // View normal para Comprador
  const [showForm, setShowForm] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    unidade: 'UN',
    curva_abc: 'Curva A',
    qualidade: 'Indeterminado',
    peso_cx: '',
    variacoes: ['']  // Array de variações, começa com um campo vazio
  });

  // Funções para manipular variações
  const handleVariacaoChange = (index, valor) => {
    const novasVariacoes = [...formData.variacoes];
    novasVariacoes[index] = valor;
    setFormData({ ...formData, variacoes: novasVariacoes });
  };

  const adicionarVariacao = () => {
    setFormData({ ...formData, variacoes: [...formData.variacoes, ''] });
  };

  const removerVariacao = (index) => {
    const novasVariacoes = formData.variacoes.filter((_, i) => i !== index);
    // Garantir que sempre há pelo menos um campo
    if (novasVariacoes.length === 0) novasVariacoes.push('');
    setFormData({ ...formData, variacoes: novasVariacoes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filtrar variações vazias
      const variacoesFiltradas = formData.variacoes.filter(v => v.trim() !== '');
      
      // Preparar dados: converter peso_cx vazio para null
      const dadosParaEnviar = {
        ...formData,
        peso_cx: formData.peso_cx === '' || formData.peso_cx === null ? null : parseFloat(formData.peso_cx),
        variacoes: variacoesFiltradas
      };
      
      if (editingProduto) {
        await axios.put(`/produtos/${editingProduto.id}`, dadosParaEnviar);
        toast({ title: "Produto atualizado com sucesso!" });
      } else {
        await axios.post('/produtos', dadosParaEnviar);
        toast({ title: "Produto cadastrado com sucesso!" });
      }
      setShowForm(false);
      setEditingProduto(null);
      setFormData({ nome: '', unidade: 'UN', curva_abc: 'Curva A', qualidade: 'Indeterminado', peso_cx: '', variacoes: [''] });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao salvar produto",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (produto) => {
    setEditingProduto(produto);
    setFormData({
      nome: produto.nome,
      unidade: produto.unidade,
      curva_abc: produto.curva_abc,
      qualidade: produto.qualidade,
      peso_cx: produto.peso_cx || '',
      variacoes: produto.variacoes && produto.variacoes.length > 0 ? produto.variacoes : ['']
    });
    setShowForm(true);
  };

  const handleDelete = async (produto) => {
    const confirmed = await showConfirmDialog(
      "Excluir Produto",
      "Tem certeza que deseja excluir?"
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/produtos/${produto.id}`);
      toast({ title: "Produto excluído com sucesso!" });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir produto",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  if (showForm) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowForm(false);
              setEditingProduto(null);
              setFormData({ nome: '', unidade: 'UN', curva_abc: 'Curva A', qualidade: 'Indeterminado', peso_cx: '', variacoes: [''] });
            }}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-button"
          >
            ← Voltar
          </button>
        </div>
        
        <h3 className="text-lg font-medium mb-6">
          {editingProduto ? 'Editar Produto' : 'Cadastrar Novo Produto'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Produto
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              required
              data-testid="produto-nome-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unidade
            </label>
            <select
              value={formData.unidade}
              onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              data-testid="produto-unidade-select"
            >
              <option value="UN">UN</option>
              <option value="CX">CX</option>
              <option value="SC">SC</option>
            </select>
          </div>
          
          {/* Campo Peso CX - só aparece se unidade != "UN" */}
          {formData.unidade !== 'UN' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Peso CX (kg) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.peso_cx}
                onChange={(e) => setFormData({ ...formData, peso_cx: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                required={formData.unidade !== 'UN'}
                placeholder="Ex: 18.5"
                data-testid="produto-peso-cx-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Peso da caixa em quilos (obrigatório para unidade CX/SC)
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Curva ABC
            </label>
            <select
              value={formData.curva_abc}
              onChange={(e) => setFormData({ ...formData, curva_abc: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              data-testid="produto-curva-select"
            >
              <option value="Curva A">Curva A</option>
              <option value="Curva B">Curva B</option>
              <option value="Curva C">Curva C</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Qualidade
            </label>
            <select
              value={formData.qualidade}
              onChange={(e) => setFormData({ ...formData, qualidade: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              data-testid="produto-qualidade-select"
            >
              <option value="Indeterminado">Indeterminado</option>
              <option value="Verde ou Maduro">Verde ou Maduro</option>
            </select>
          </div>
          
          {/* Campo Variações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Variações (Opcional)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Informe se o produto possui variações específicas (tamanhos, tipos, embalagens, etc.)
            </p>
            <div className="space-y-2">
              {formData.variacoes.map((variacao, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={variacao}
                    onChange={(e) => handleVariacaoChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                    placeholder={`Variação ${index + 1} (ex: Tamanho P, Grande, 500g...)`}
                    data-testid={`produto-variacao-input-${index}`}
                  />
                  {formData.variacoes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removerVariacao(index)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      title="Remover variação"
                      data-testid={`remove-variacao-${index}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={adicionarVariacao}
              className="mt-3 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium flex items-center space-x-2"
              data-testid="add-variacao-button"
            >
              <span>+</span>
              <span>Adicionar Variação</span>
            </button>
          </div>
          
          <div className="flex space-x-4">
            <button
              type="submit"
              className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
              data-testid="save-produto-button"
            >
              {editingProduto ? 'Atualizar' : 'Cadastrar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingProduto(null);
                setFormData({ nome: '', unidade: 'UN', curva_abc: 'Curva A', qualidade: 'Indeterminado', peso_cx: '', variacoes: [''] });
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              data-testid="cancel-produto-button"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center space-x-2"
          data-testid="add-produto-button"
        >
          <span>+</span>
          <span>Cadastrar Produto</span>
        </button>
      </div>
      
      {produtos.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">📦</div>
          <p>Nenhum produto cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {produtos.map((produto) => (
            <div key={produto.id} className="bg-gray-50 rounded-lg p-4 border">
              <h4 className="font-medium text-gray-900 mb-2">{produto.nome}</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Unidade: {produto.unidade}</p>
                {produto.unidade !== 'UN' && produto.peso_cx && (
                  <p>Peso CX: {produto.peso_cx} kg</p>
                )}
                <p>Curva: {produto.curva_abc}</p>
                <p>Qualidade: {produto.qualidade}</p>
                {produto.variacoes && produto.variacoes.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-gray-700">Variações:</p>
                    <ul className="list-disc list-inside ml-2">
                      {produto.variacoes.map((variacao, idx) => (
                        <li key={idx} className="text-xs text-gray-600">{variacao}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => handleEdit(produto)}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  data-testid={`edit-produto-${produto.id}`}
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(produto)}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                  data-testid={`delete-produto-${produto.id}`}
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Usuarios Tab Component
const UsuariosTab = ({ usuarios, onRefresh, showConfirmDialog }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [searchProdutos, setSearchProdutos] = useState('');
  const [formData, setFormData] = useState({
    nome_completo: '',
    login: '',
    senha: '',
    tipo_usuario: 'Gerente',
    filial: '',
    produtos_vinculados: []
  });

  // Carregar produtos quando abre o formulário
  React.useEffect(() => {
    if (showForm) {
      loadProdutos();
    }
  }, [showForm]);

  const loadProdutos = async () => {
    try {
      const produtosData = await fetchAllPaginated('/produtos');
      setProdutos(produtosData);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleProdutoToggle = (produtoId) => {
    const produtos_vinculados = formData.produtos_vinculados || [];
    const isSelected = produtos_vinculados.includes(produtoId);
    
    if (isSelected) {
      setFormData({
        ...formData,
        produtos_vinculados: produtos_vinculados.filter(id => id !== produtoId)
      });
    } else {
      setFormData({
        ...formData,
        produtos_vinculados: [...produtos_vinculados, produtoId]
      });
    }
  };

  const filteredProdutos = produtos.filter(produto =>
    produto.nome.toLowerCase().includes(searchProdutos.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUsuario) {
        await axios.put(`/usuarios/${editingUsuario.id}`, formData);
        toast({ title: "Usuário atualizado com sucesso!" });
      } else {
        await axios.post('/usuarios', formData);
        toast({ title: "Usuário cadastrado com sucesso!" });
      }
      setShowForm(false);
      setEditingUsuario(null);
      setFormData({
        nome_completo: '',
        login: '',
        senha: '',
        tipo_usuario: 'Gerente',
        filial: '',
        produtos_vinculados: []
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao salvar usuário",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nome_completo: usuario.nome_completo,
      login: usuario.login,
      senha: '',
      tipo_usuario: usuario.tipo_usuario,
      filial: usuario.filial || '',
      produtos_vinculados: usuario.produtos_vinculados || []
    });
    setSearchProdutos(''); // Limpar busca ao editar
    setShowForm(true);
  };

  const handleDelete = async (usuario) => {
    console.log('handleDelete called with user:', usuario.nome_completo);
    
    // Prevent deletion of master user
    if (usuario.login === 'Thiago') {
      console.log('Preventing master user deletion');
      toast({
        title: "Operação não permitida",
        description: "Não é possível excluir o usuário master",
        variant: "destructive"
      });
      return;
    }

    console.log('Showing confirmation dialog');
    const confirmed = await showConfirmDialog(
      "Excluir Usuário",
      "Tem certeza que deseja excluir este usuário? Esta ação não poderá ser desfeita."
    );
    
    if (!confirmed) return;

    try {
      console.log(`Starting deletion for user: ${usuario.id} (${usuario.nome_completo})`);
      
      const response = await axios.delete(`/usuarios/${usuario.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log(`Delete response status:`, response.status);
      console.log(`Delete response data:`, response.data);
      
      toast({ 
        title: "Usuário excluído com sucesso!",
        description: `${usuario.nome_completo} foi removido do sistema.`
      });
      
      console.log('Calling onRefresh to update list');
      onRefresh();
      
    } catch (error) {
      console.error('Delete error details:', error);
      console.error('Delete error response:', error.response);
      
      let errorMessage = "Erro interno do servidor";
      
      if (error.response?.status === 404) {
        errorMessage = "Usuário não encontrado";
      } else if (error.response?.status === 403) {
        errorMessage = "Permissão negada para esta operação";
      } else if (error.response?.status === 401) {
        errorMessage = "Sessão expirada. Faça login novamente.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast({
        title: "Erro ao excluir usuário",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  if (showForm) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowForm(false);
              setEditingUsuario(null);
              setSearchProdutos('');
              setFormData({
                nome_completo: '',
                login: '',
                senha: '',
                tipo_usuario: 'Gerente',
                filial: '',
                produtos_vinculados: []
              });
            }}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-button"
          >
            ← Voltar
          </button>
        </div>
        
        <h3 className="text-lg font-medium mb-6">
          {editingUsuario ? 'Editar Usuário' : 'Cadastrar Novo Usuário'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo
            </label>
            <input
              type="text"
              value={formData.nome_completo}
              onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              required
              data-testid="usuario-nome-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login
            </label>
            <input
              type="text"
              value={formData.login}
              onChange={(e) => setFormData({ ...formData, login: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              required
              data-testid="usuario-login-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha {editingUsuario && '(deixe em branco para manter a atual)'}
            </label>
            <input
              type="password"
              value={formData.senha}
              onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              required={!editingUsuario}
              data-testid="usuario-senha-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Usuário
            </label>
            <select
              value={formData.tipo_usuario}
              onChange={(e) => setFormData({ ...formData, tipo_usuario: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              data-testid="usuario-tipo-select"
            >
              <option value="Gerente">Gerente</option>
              <option value="Fornecedor">Fornecedor</option>
              <option value="Motorista">Motorista</option>
              <option value="Administrador">Administrador</option>
            </select>
          </div>
          
          {formData.tipo_usuario === 'Gerente' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filial
              </label>
              <select
                value={formData.filial}
                onChange={(e) => setFormData({ ...formData, filial: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                data-testid="usuario-filial-select"
              >
                <option value="">Selecione uma filial</option>
                <option value="GOIABEIRAS">GOIABEIRAS</option>
                <option value="NOVA AMÉRICA">NOVA AMÉRICA</option>
                <option value="NOVA ROSA">NOVA ROSA</option>
                <option value="PORTO BELO">PORTO BELO</option>
                <option value="SANTANA">SANTANA</option>
                <option value="SÃO FRANCISCO">SÃO FRANCISCO</option>
                <option value="SÃO PEDRO">SÃO PEDRO</option>
                <option value="VALE">VALE</option>
                <option value="VIANA">VIANA</option>
              </select>
            </div>
          )}

          {formData.tipo_usuario === 'Fornecedor' && (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produtos Vinculados
                </label>
              <div className="border border-gray-300 rounded-lg p-3">
                {/* Campo de busca integrado */}
                <input
                  type="text"
                  value={searchProdutos}
                  onChange={(e) => setSearchProdutos(e.target.value)}
                  placeholder="Digite para filtrar produtos..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-600 mb-3"
                  data-testid="search-produtos-input"
                />
                
                {/* Lista de produtos com checkboxes */}
                <div className="max-h-48 overflow-y-auto">
                  {filteredProdutos.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      {searchProdutos ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {filteredProdutos.map((produto) => (
                        <label
                          key={produto.id}
                          className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                          data-testid={`produto-checkbox-${produto.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={(formData.produtos_vinculados || []).includes(produto.id)}
                            onChange={() => handleProdutoToggle(produto.id)}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{produto.nome}</div>
                            <div className="text-sm text-gray-500">
                              {produto.unidade} • {produto.curva_abc} • {produto.qualidade}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Contador de produtos selecionados */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600">
                    {(formData.produtos_vinculados || []).length} produto(s) selecionado(s)
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-4">
            <button
              type="submit"
              className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
              data-testid="save-usuario-button"
            >
              {editingUsuario ? 'Atualizar' : 'Cadastrar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingUsuario(null);
                setSearchProdutos('');
                setFormData({
                  nome_completo: '',
                  login: '',
                  senha: '',
                  tipo_usuario: 'Gerente',
                  filial: '',
                  produtos_vinculados: []
                });
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              data-testid="cancel-usuario-button"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center space-x-2"
          data-testid="add-usuario-button"
        >
          <span>+</span>
          <span>Cadastrar Usuário</span>
        </button>
      </div>
      
      {usuarios.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">👥</div>
          <p>Nenhum usuário cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {usuarios.map((usuario) => (
            <div key={usuario.id} className="bg-gray-50 rounded-lg p-4 border">
              <h4 className="font-medium text-gray-900 mb-2">{usuario.nome_completo}</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Login: {usuario.login}</p>
                <p>Tipo: {usuario.tipo_usuario}</p>
                {usuario.filial && <p>Filial: {usuario.filial}</p>}
                {usuario.tipo_usuario === 'Fornecedor' && (
                  <p>
                    Produtos: 
                    <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {(usuario.produtos_vinculados || []).length} vinculado(s)
                    </span>
                  </p>
                )}
              </div>
              {usuario.login !== 'Thiago' && (
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleEdit(usuario)}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                    data-testid={`edit-usuario-${usuario.id}`}
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(usuario)}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                    data-testid={`delete-usuario-${usuario.id}`}
                  >
                    Excluir
                  </button>
                </div>
              )}
              {usuario.login === 'Thiago' && (
                <div className="mt-4">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm">
                    Usuário Master
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// ============= NOVO PEDIDOS TAB - REFORMULADO =============
const PedidosTab = ({ pedidos, produtos, cotacoes = [], promocoes = [], onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showSelectFilial, setShowSelectFilial] = useState(false);
  const [selectedFilial, setSelectedFilial] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [pedidoEmEdicao, setPedidoEmEdicao] = useState(null);
  const [produtosEditor, setProdutosEditor] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(''); // Estado para texto da pesquisa
  
  // Estado local para pedidos - evita scroll reset ao atualizar
  const [pedidosLocal, setPedidosLocal] = useState(pedidos);
  
  // Referência para salvar posição de scroll antes de abrir modal
  const scrollPositionRef = React.useRef(0);
  
  // Sincronizar com props quando mudar (ex: refresh global)
  useEffect(() => {
    setPedidosLocal(pedidos);
  }, [pedidos]);
  
  // Restaurar posição de scroll quando fechar o editor
  useEffect(() => {
    if (!showEditor && scrollPositionRef.current > 0) {
      // Usar requestAnimationFrame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
      });
    }
  }, [showEditor]);
  
  // Função para recarregar pedidos localmente (sem causar scroll reset)
  const recarregarPedidosLocal = async () => {
    try {
      const pedidosData = await fetchAllPaginated('/pedidos');
      setPedidosLocal(pedidosData);
    } catch (error) {
      console.error('Erro ao recarregar pedidos:', error);
    }
  };

  // Função para verificar se um produto está em promoção finalizada
  const isProdutoEmPromocao = (produtoId) => {
    if (!promocoes || promocoes.length === 0) return false;
    
    // Verificar se produto está em alguma promoção finalizada
    return promocoes.some(promocao => {
      if (!promocao.finalizada) return false;
      if (!promocao.produtos || promocao.produtos.length === 0) return false;
      
      // Verificar se produto está na lista de produtos da promoção
      return promocao.produtos.some(p => {
        if (!p.preco_promocao || p.preco_promocao <= 0) return false;
        
        // Verificar match direto pelo produto_id
        if (p.produto_id === produtoId) return true;
        
        // Retrocompatibilidade: verificar se produto_id contém o ID do produto (formato antigo "uuid_variacao")
        if (p.produto_id && p.produto_id.includes('_')) {
          const uuidParte = p.produto_id.split('_')[0];
          if (uuidParte === produtoId) return true;
        }
        
        return false;
      });
    });
  };
  
  // Função para obter o valor promocional de um produto
  const getValorPromocional = (produtoId) => {
    if (!promocoes || promocoes.length === 0) return null;
    
    // Buscar produto em promoções finalizadas
    for (const promocao of promocoes) {
      if (!promocao.finalizada) continue;
      if (!promocao.produtos || promocao.produtos.length === 0) continue;
      
      // Primeiro tentar match direto
      let produtoPromocao = promocao.produtos.find(p => 
        p.produto_id === produtoId && p.preco_promocao && p.preco_promocao > 0
      );
      
      // Se não encontrou, tentar retrocompatibilidade (formato antigo "uuid_variacao")
      if (!produtoPromocao) {
        produtoPromocao = promocao.produtos.find(p => {
          if (!p.preco_promocao || p.preco_promocao <= 0) return false;
          if (p.produto_id && p.produto_id.includes('_')) {
            const uuidParte = p.produto_id.split('_')[0];
            return uuidParte === produtoId;
          }
          return false;
        });
      }
      
      if (produtoPromocao) {
        return produtoPromocao.preco_promocao;
      }
    }
    
    return null;
  };

  // Verificar se gerente pode criar pedido
  const podeGerCriarPedido = async () => {
    if (usuario.tipo_usuario !== 'Gerente') return true;
    
    try {
      const response = await axios.get('/pedidos/gerente/ativo');
      if (response.data.tem_pedido_ativo) {
        toast({
          title: "Pedido ativo existente",
          description: "Você já possui um pedido ativo. Finalize ou exclua o pedido atual antes de criar um novo.",
          variant: "destructive"
        });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Erro ao verificar pedido ativo:', error);
      return true;
    }
  };

  // Criar novo pedido (apenas GERENTE)
  const handleNovoPedido = async () => {
    // Verificar se é gerente
    if (usuario.tipo_usuario !== 'Gerente') {
      toast({
        title: "Acesso negado",
        description: "Apenas gerentes podem criar novos pedidos.",
        variant: "destructive"
      });
      return;
    }

    const podecriar = await podeGerCriarPedido();
    if (!podecriar) return;

    // Criar pedido com filial do gerente
    criarPedidoVazio(usuario.filial);
  };

  const criarPedidoVazio = async (filial) => {
    try {
      setLoading(true);
      const response = await axios.post('/pedidos', { filial });
      setPedidoEmEdicao(response.data);
      inicializarEditor(response.data);
      setShowSelectFilial(false);
      toast({ title: `Pedido #${response.data.numero} criado!` });
      // Atualizar lista local sem causar scroll reset
      await recarregarPedidosLocal();
    } catch (error) {
      toast({
        title: "Erro ao criar pedido",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Inicializar editor com TODOS os produtos
  const inicializarEditor = (pedido) => {
    const produtosOrdenados = [...produtos].sort((a, b) => a.nome.localeCompare(b.nome));
    
    const produtosComDados = produtosOrdenados.map(produto => {
      // Buscar menor valor ATIVO ou da última cotação vencedora
      let valor = null;
      
      // 1. PRIMEIRO: Buscar cotações ATIVAS com propostas já enviadas
      const cotacoesAtivas = cotacoes
        .filter(c => 
          c.produtos_ids && 
          c.produtos_ids.includes(produto.id) && 
          c.status_cotacao === 'Ativa' &&
          c.fornecedores && 
          Object.keys(c.fornecedores).length > 0
        )
        .sort((a, b) => new Date(b.data_criacao) - new Date(a.data_criacao));
      
      if (cotacoesAtivas.length > 0) {
        // Buscar o MENOR PREÇO entre todos os fornecedores que já enviaram proposta
        let menorValorKg = Infinity;
        
        for (const cotacao of cotacoesAtivas) {
          const fornecedoresObj = cotacao.fornecedores || {};
          
          for (const [fornecedorNome, dadosFornecedor] of Object.entries(fornecedoresObj)) {
            const produtosFornecedor = dadosFornecedor.produtos || {};
            const proposta = produtosFornecedor[produto.id];
            
            if (proposta) {
              let valorKg = null;
              
              // Produto UN: usar preco_kg_un diretamente
              if (produto.unidade === 'UN' && proposta.preco_kg_un) {
                valorKg = proposta.preco_kg_un;
              }
              // Produto CX/SC: calcular a partir de valor_caixa / quantidade_kg
              else if (proposta.valor_caixa && proposta.quantidade_kg) {
                valorKg = proposta.valor_caixa / proposta.quantidade_kg;
              }
              
              if (valorKg && valorKg < menorValorKg) {
                menorValorKg = valorKg;
              }
            }
          }
        }
        
        if (menorValorKg !== Infinity) {
          // Adicionar 5% sobre o valor vencedor
          const valorCom5Porcento = menorValorKg * 1.05;
          valor = parseFloat(valorCom5Porcento.toFixed(2));
        }
      }
      
      // 2. SE NÃO HÁ COTAÇÃO ATIVA: Buscar cotação ENCERRADA vencedora (comportamento antigo)
      if (valor === null) {
        const cotacoesEncerradas = cotacoes
          .filter(c => c.produtos_ids && c.produtos_ids.includes(produto.id) && c.status_cotacao === 'Encerrada' && c.vencedores)
          .sort((a, b) => new Date(b.data_criacao) - new Date(a.data_criacao));
        
        if (cotacoesEncerradas.length > 0) {
          const ultimaCotacao = cotacoesEncerradas[0];
          const vencedor = ultimaCotacao.vencedores?.[produto.id];
          
          if (vencedor) {
            let valorKg = null;
            
            // Produto UN: usar preco_kg_un diretamente
            if (produto.unidade === 'UN' && vencedor.preco_kg_un) {
              valorKg = vencedor.preco_kg_un;
            }
            // Produto CX/SC: calcular a partir de valor_caixa / quantidade_kg
            else if (vencedor.valor_caixa && vencedor.quantidade_kg) {
              valorKg = vencedor.valor_caixa / vencedor.quantidade_kg;
            }
            // Fallback: se não tiver quantidade_kg, usar valor da caixa
            else if (vencedor.valor_caixa) {
              valorKg = vencedor.valor_caixa;
            }
            
            if (valorKg) {
              // Adicionar 5% sobre o valor vencedor
              const valorCom5Porcento = valorKg * 1.05;
              valor = parseFloat(valorCom5Porcento.toFixed(2));
            }
          }
        }
      }

      // Buscar dados existentes no pedido
      const produtoExistente = pedido.produtos?.find(p => p.produto_id === produto.id);

      return {
        produto_id: produto.id,
        nome_produto: produto.nome,
        qualidade_cadastro: produto.qualidade || 'Indeterminado',
        quantidade: produtoExistente?.quantidade || '',
        valor: valor,
        pediu_meia_cx: produtoExistente?.pediu_meia_cx || '',
        qualidade: produtoExistente?.qualidade || '',
        qtd_verde: produtoExistente?.qtd_verde || '',
        qtd_maduro: produtoExistente?.qtd_maduro || ''
      };
    });

    // Se for modo VISUALIZAÇÃO, filtrar apenas produtos com quantidade
    const produtosFiltrados = pedido.visualizacao_readonly 
      ? produtosComDados.filter(p => p.quantidade && parseFloat(p.quantidade) > 0)
      : produtosComDados;

    setProdutosEditor(produtosFiltrados);
    setShowEditor(true);
  };

  // Abrir pedido para VISUALIZAÇÃO (readonly - somente leitura)
  const handleVisualizarPedido = (pedido) => {
    // Salvar posição de scroll antes de abrir modal
    scrollPositionRef.current = window.scrollY;
    // Sempre abrir em modo visualização (readonly)
    setPedidoEmEdicao({ ...pedido, visualizacao_readonly: true });
    inicializarEditor({ ...pedido, visualizacao_readonly: true });
    setSearchText(''); // Limpar pesquisa ao abrir visualização
  };

  // Abrir pedido para EDIÇÃO
  const handleEditarPedido = (pedido) => {
    // Verificar se GERENTE está tentando editar pedido finalizado
    if (usuario.tipo_usuario === 'Gerente' && pedido.finalizado) {
      toast({
        title: "Pedido não editável",
        description: "Este pedido foi finalizado e não pode mais ser editado. Use o botão VISUALIZAR para consultar.",
        variant: "destructive"
      });
      return;
    }
    
    // Salvar posição de scroll antes de abrir modal
    scrollPositionRef.current = window.scrollY;
    // COMPRADOR ou GERENTE com pedido não finalizado - abrir para edição
    setPedidoEmEdicao(pedido);
    inicializarEditor(pedido);
    setSearchText(''); // Limpar pesquisa ao abrir editor
  };

  // Filtrar produtos baseado no texto de pesquisa
  const getProdutosFiltrados = () => {
    if (!searchText || searchText.trim() === '') {
      return produtosEditor;
    }
    
    const searchLower = searchText.toLowerCase().trim();
    return produtosEditor.filter(produto => 
      produto && produto.nome_produto && produto.nome_produto.toLowerCase().includes(searchLower)
    );
  };

  // Atualizar campo do produto
  const handleCampoChange = (produtoId, campo, valor) => {
    const novosProdutos = [...produtosEditor];
    const index = novosProdutos.findIndex(p => p.produto_id === produtoId);
    
    if (index === -1) return; // Produto não encontrado
    
    // Validação especial para quantidade - só aceitar inteiros ou .5
    if (campo === 'quantidade' && valor) {
      const quantidade = parseFloat(valor);
      if (!isNaN(quantidade)) {
        const parteDecimal = quantidade - Math.floor(quantidade);
        // Se não for inteiro e não for .5, avisar
        if (parteDecimal !== 0 && Math.abs(parteDecimal - 0.5) > 0.01) {
          toast({
            title: "Formato inválido",
            description: "A quantidade deve ser um número inteiro (1, 2, 3...) ou ter .5 (0.5, 1.5, 2.5...)",
            variant: "destructive"
          });
          // Não atualizar o valor
          return;
        }
      }
    }
    
    novosProdutos[index][campo] = valor;

    // Se limpar quantidade, limpar outros campos também
    if (campo === 'quantidade' && !valor) {
      novosProdutos[index].pediu_meia_cx = '';
      novosProdutos[index].qualidade = '';
      novosProdutos[index].qtd_verde = '';
      novosProdutos[index].qtd_maduro = '';
    }
    
    // Se quantidade não tiver .5, limpar pediu_meia_cx
    if (campo === 'quantidade' && valor) {
      const quantidade = parseFloat(valor);
      const parteDecimal = quantidade - Math.floor(quantidade);
      const temMeiaCaixa = Math.abs(parteDecimal - 0.5) < 0.01;
      
      if (!temMeiaCaixa) {
        novosProdutos[index].pediu_meia_cx = '';
      }
    }

    // Se mudar qualidade para algo diferente de "Ambos", limpar qtd verde/maduro
    if (campo === 'qualidade' && valor !== 'Ambos') {
      novosProdutos[index].qtd_verde = '';
      novosProdutos[index].qtd_maduro = '';
    }

    setProdutosEditor(novosProdutos);
  };

  // Validar e finalizar pedido
  const handleFinalizar = async () => {
    // Filtrar produtos com quantidade
    const produtosValidos = produtosEditor.filter(p => p.quantidade && parseFloat(p.quantidade) > 0);

    if (produtosValidos.length === 0) {
      toast({
        title: "Pedido vazio",
        description: "Adicione pelo menos um produto com quantidade antes de finalizar.",
        variant: "destructive"
      });
      return;
    }

    // VALIDAÇÃO 1: Formato de QUANTIDADE (apenas inteiros ou .5)
    for (const produto of produtosValidos) {
      const quantidade = parseFloat(produto.quantidade);
      const parteDecimal = quantidade - Math.floor(quantidade);
      
      // Permitir apenas inteiros (0.0) ou valores com .5 (0.5)
      if (parteDecimal !== 0 && Math.abs(parteDecimal - 0.5) > 0.01) {
        toast({
          title: "Formato de quantidade inválido",
          description: `${produto.nome_produto}: A quantidade deve ser um número inteiro (1, 2, 3...) ou ter .5 (0.5, 1.5, 2.5...). Valores como ${quantidade} não são permitidos.`,
          variant: "destructive"
        });
        return;
      }
    }

    // VALIDAÇÃO 2: Campo PEDIU MEIA CX? obrigatório quando quantidade tem .5
    for (const produto of produtosValidos) {
      const quantidade = parseFloat(produto.quantidade);
      const parteDecimal = quantidade - Math.floor(quantidade);
      
      // Se tem .5, campo PEDIU MEIA CX? é obrigatório
      if (Math.abs(parteDecimal - 0.5) < 0.01) {
        if (!produto.pediu_meia_cx || produto.pediu_meia_cx === '') {
          toast({
            title: "Campo obrigatório",
            description: `${produto.nome_produto}: Quando a quantidade é ${quantidade}, você deve preencher o campo "PEDIU MEIA CX?" com uma das opções disponíveis.`,
            variant: "destructive"
          });
          return;
        }
      }
    }

    // VALIDAÇÃO 3: Campo QUALIDADE obrigatório quando produto tem qualidade VERDE ou MADURO no cadastro
    for (const produto of produtosValidos) {
      // Verificar se o produto tem qualidade_cadastro como VERDE, MADURO ou VERDE OU MADURO
      const temQualidadeObrigatoria = produto.qualidade_cadastro === 'Tudo verde' || 
                                      produto.qualidade_cadastro === 'Tudo maduro' ||
                                      produto.qualidade_cadastro === 'Verde ou Maduro';
      
      if (temQualidadeObrigatoria) {
        if (!produto.qualidade || produto.qualidade === '') {
          toast({
            title: "Campo QUALIDADE obrigatório",
            description: `${produto.nome_produto}: Este produto tem qualidade ${produto.qualidade_cadastro} no cadastro. Você deve preencher o campo QUALIDADE no pedido antes de finalizar.`,
            variant: "destructive"
          });
          return;
        }
      }
    }

    // Validar produtos com "Ambos"
    for (const produto of produtosValidos) {
      if (produto.qualidade === 'Ambos') {
        const qtd = parseFloat(produto.quantidade);
        const qtdVerde = parseFloat(produto.qtd_verde) || 0;
        const qtdMaduro = parseFloat(produto.qtd_maduro) || 0;
        
        if (Math.abs((qtdVerde + qtdMaduro) - qtd) > 0.01) {
          toast({
            title: "Erro de validação",
            description: `${produto.nome_produto}: A soma de QTD VERDE (${qtdVerde}) + QTD MADURO (${qtdMaduro}) deve ser igual à QUANTIDADE (${qtd}).`,
            variant: "destructive"
          });
          return;
        }
      }
    }

    try {
      setLoading(true);

      // Remover campo qualidade_cadastro e converter strings vazias em null antes de enviar
      const produtosParaEnviar = produtosValidos.map(({ qualidade_cadastro, ...produto }) => ({
        ...produto,
        pediu_meia_cx: produto.pediu_meia_cx || null,
        qualidade: produto.qualidade || null,
        qtd_verde: produto.qtd_verde ? parseFloat(produto.qtd_verde) : null,
        qtd_maduro: produto.qtd_maduro ? parseFloat(produto.qtd_maduro) : null
      }));

      // Atualizar produtos
      await axios.put(`/pedidos/${pedidoEmEdicao.id}/produtos`, produtosParaEnviar);

      // Finalizar pedido
      await axios.put(`/pedidos/${pedidoEmEdicao.id}/finalizar`);

      toast({ title: "Pedido finalizado com sucesso!" });
      setShowEditor(false);
      setPedidoEmEdicao(null);
      setProdutosEditor([]);
      // Atualizar lista local sem causar scroll reset
      await recarregarPedidosLocal();
    } catch (error) {
      toast({
        title: "Erro ao finalizar pedido",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Excluir pedido
  const handleExcluirPedido = async (pedido) => {
    const confirmed = await showConfirmDialog(
      "Excluir Pedido",
      `Tem certeza que deseja excluir o pedido #${pedido.numero}? Esta ação não poderá ser desfeita.`
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/pedidos/${pedido.id}`);
      toast({ title: "Pedido excluído com sucesso!" });
      // Atualizar lista local sem causar scroll reset
      await recarregarPedidosLocal();
    } catch (error) {
      toast({
        title: "Erro ao excluir pedido",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Exportar pedido para impressão
  const exportarPedidoParaImpressao = () => {
    if (!pedidoEmEdicao) return;

    const printWindow = window.open('', '_blank');
    
    // Filtrar apenas produtos com quantidade
    const produtosComQuantidade = produtosEditor.filter(p => p.quantidade && parseFloat(p.quantidade) > 0);
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Pedido ${pedidoEmEdicao.numero}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            font-size: 11pt;
          }
          .header {
            border-bottom: 3px solid #4f46e5;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 24pt;
            color: #4f46e5;
            margin-bottom: 5px;
          }
          .header h2 {
            font-size: 18pt;
            color: #6b7280;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 25px;
            background-color: #f3f4f6;
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .info-item {
            padding: 5px 0;
          }
          .info-item strong {
            font-weight: bold;
            display: block;
            color: #374151;
            margin-bottom: 3px;
            font-size: 9pt;
            text-transform: uppercase;
          }
          .info-item span {
            color: #1f2937;
            font-size: 11pt;
          }
          .badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 9pt;
            font-weight: bold;
          }
          .badge-success {
            background-color: #d1fae5;
            color: #065f46;
          }
          .badge-warning {
            background-color: #fef3c7;
            color: #92400e;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #4f46e5;
            color: white;
            border: 1px solid #4338ca;
            padding: 12px 8px;
            text-align: left;
            font-weight: bold;
            font-size: 10pt;
            text-transform: uppercase;
          }
          td {
            border: 1px solid #d1d5db;
            padding: 10px 8px;
            font-size: 10pt;
          }
          tbody tr:nth-child(even) {
            background-color: #f9fafb;
          }
          tbody tr:hover {
            background-color: #f3f4f6;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 9pt;
          }
          .total-row {
            background-color: #e0e7ff !important;
            font-weight: bold;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PEDIDO #${pedidoEmEdicao.numero}</h1>
          <h2>${pedidoEmEdicao.filial}</h2>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <strong>Status</strong>
            <span>
              <span class="badge ${pedidoEmEdicao.finalizado ? 'badge-success' : 'badge-warning'}">
                ${pedidoEmEdicao.finalizado ? 'Finalizado' : 'Aberto'}
              </span>
            </span>
          </div>
          <div class="info-item">
            <strong>Data de Criação</strong>
            <span>${new Date(pedidoEmEdicao.criado_em).toLocaleDateString('pt-BR', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
          <div class="info-item">
            <strong>Criado por</strong>
            <span>${pedidoEmEdicao.criado_por_nome || 'Sistema'} (${pedidoEmEdicao.criado_por_tipo || 'N/A'})</span>
          </div>
          ${pedidoEmEdicao.finalizado && pedidoEmEdicao.finalizado_em ? `
          <div class="info-item">
            <strong>Finalizado em</strong>
            <span>${new Date(pedidoEmEdicao.finalizado_em).toLocaleDateString('pt-BR', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
          ` : ''}
        </div>

        <h3 style="margin-bottom: 10px; color: #374151; font-size: 14pt;">Produtos do Pedido</h3>
        
        <table>
          <thead>
            <tr>
              <th style="width: 40%;">PRODUTO</th>
              <th style="width: 15%; text-align: center;">QUANTIDADE</th>
              <th style="width: 15%; text-align: center;">VALOR (R$/KG)</th>
              <th style="width: 15%; text-align: center;">MEIA CX?</th>
              <th style="width: 15%; text-align: center;">QUALIDADE</th>
            </tr>
          </thead>
          <tbody>
            ${produtosComQuantidade.map(produto => `
              <tr>
                <td><strong>${produto.nome_produto}</strong></td>
                <td style="text-align: center;">${parseFloat(produto.quantidade).toFixed(2)}</td>
                <td style="text-align: center;">${produto.valor ? produto.valor.toFixed(2) : '—'}</td>
                <td style="text-align: center;">${produto.pediu_meia_cx || '—'}</td>
                <td style="text-align: center;">
                  ${produto.qualidade || '—'}
                  ${produto.qualidade === 'Ambos' ? `<br><small style="color: #6b7280;">(Verde: ${produto.qtd_verde || 0} | Maduro: ${produto.qtd_maduro || 0})</small>` : ''}
                </td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="1"><strong>TOTAL DE PRODUTOS</strong></td>
              <td colspan="4" style="text-align: center;"><strong>${produtosComQuantidade.length} itens</strong></td>
            </tr>
          </tbody>
        </table>

        <div class="footer">
          <p><strong>VIA COTAÇÕES</strong> - Sistema de Gestão de Pedidos</p>
          <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  // Renderizar linha da tabela
  const renderLinhaProduto = (produto, index) => {
    const temQuantidade = produto.quantidade && parseFloat(produto.quantidade) > 0;
    const isReadonly = pedidoEmEdicao?.visualizacao_readonly || false;
    const emPromocao = isProdutoEmPromocao(produto.produto_id);

    return (
      <tr key={produto.produto_id} className={emPromocao ? 'bg-green-50' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
        {/* PRODUTO */}
        <td className="px-4 py-3 text-sm font-medium text-gray-900">
          <div>
            <div>
              {produto.nome_produto}
              {emPromocao && <span className="ml-2 px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full font-medium">🎯 PROMOÇÃO</span>}
            </div>
            {emPromocao && (() => {
              const valorPromocional = getValorPromocional(produto.produto_id);
              if (valorPromocional) {
                return (
                  <div className="text-xs text-green-700 font-semibold mt-1">
                    PROMOÇÃO - R$ {valorPromocional.toFixed(2)}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </td>

        {/* QUANTIDADE */}
        <td className="px-4 py-3">
          <input
            type="number"
            step="0.01"
            value={produto.quantidade}
            onChange={(e) => handleCampoChange(produto.produto_id, 'quantidade', e.target.value)}
            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-center"
            placeholder="0"
            disabled={isReadonly}
          />
        </td>

        {/* VALOR */}
        <td className="px-4 py-3 text-sm text-gray-700 text-center">
          {produto.valor ? `R$ ${produto.valor.toFixed(2)}` : ''}
        </td>

        {/* PEDIU MEIA CX? */}
        <td className="px-4 py-3">
          {(() => {
            const quantidade = parseFloat(produto.quantidade) || 0;
            const parteDecimal = quantidade - Math.floor(quantidade);
            const temMeiaCaixa = Math.abs(parteDecimal - 0.5) < 0.01;
            const ehInteiro = parteDecimal === 0 && quantidade > 0;
            
            // Mostrar campo se tiver .5 na quantidade (obrigatório)
            if (temQuantidade && temMeiaCaixa) {
              return (
                <select
                  value={produto.pediu_meia_cx}
                  onChange={(e) => handleCampoChange(produto.produto_id, 'pediu_meia_cx', e.target.value)}
                  className={`w-full px-2 py-1 border rounded text-sm ${
                    !produto.pediu_meia_cx ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isReadonly}
                >
                  <option value="">Selecione... *</option>
                  <option value="Mandar meia cx">Mandar meia cx</option>
                  <option value="Não mandar meia cx">Não mandar meia cx</option>
                </select>
              );
            } else if (temQuantidade && ehInteiro) {
              // Para números inteiros, opção "Manter pedido" (não obrigatório)
              return (
                <select
                  value={produto.pediu_meia_cx || ''}
                  onChange={(e) => handleCampoChange(produto.produto_id, 'pediu_meia_cx', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  disabled={isReadonly}
                >
                  <option value="">— (ajustável)</option>
                  <option value="Manter pedido">Manter pedido</option>
                </select>
              );
            } else {
              return <span className="text-gray-400">—</span>;
            }
          })()}
        </td>

        {/* QUALIDADE */}
        <td className="px-4 py-3">
          {!temQuantidade ? (
            <span className="text-gray-400">—</span>
          ) : produto.qualidade_cadastro === 'Verde ou Maduro' ? (
            <div className="space-y-2">
              <select
                value={produto.qualidade}
                onChange={(e) => handleCampoChange(produto.produto_id, 'qualidade', e.target.value)}
                className={`w-full px-2 py-1 border rounded text-sm ${
                  !produto.qualidade ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                disabled={isReadonly}
              >
                <option value="">Selecione... *</option>
                <option value="Tudo verde">Tudo verde</option>
                <option value="Tudo maduro">Tudo maduro</option>
                <option value="Ambos">Ambos</option>
              </select>

              {produto.qualidade === 'Ambos' && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">QTD Verde</label>
                    <input
                      type="number"
                      step="0.01"
                      value={produto.qtd_verde}
                      onChange={(e) => handleCampoChange(produto.produto_id, 'qtd_verde', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="0"
                      disabled={isReadonly}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">QTD Maduro</label>
                    <input
                      type="number"
                      step="0.01"
                      value={produto.qtd_maduro}
                      onChange={(e) => handleCampoChange(produto.produto_id, 'qtd_maduro', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="0"
                      disabled={isReadonly}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* Modal de seleção de filial removido - não mais necessário (apenas gerentes podem criar) */}

      {/* Editor de Pedido */}
      {showEditor && pedidoEmEdicao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold">
                  Pedido #{pedidoEmEdicao.numero} - {pedidoEmEdicao.filial}
                  {pedidoEmEdicao.visualizacao_readonly && (
                    <span className="ml-3 text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded">
                      Visualização
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600">
                  {pedidoEmEdicao.visualizacao_readonly 
                    ? 'Pedido finalizado - somente visualização' 
                    : 'Preencha a quantidade dos produtos desejados'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEditor(false);
                  setPedidoEmEdicao(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Barra de Pesquisa - Sticky no topo */}
              <div className="sticky top-0 bg-white z-10 pb-4 mb-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="🔍 Pesquisar produto..."
                    className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <div className="absolute left-3 top-3 text-gray-400">
                    🔍
                  </div>
                  {searchText && (
                    <button
                      onClick={() => setSearchText('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      title="Limpar pesquisa"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchText && (
                  <p className="mt-2 text-sm text-gray-600">
                    {getProdutosFiltrados().length} {getProdutosFiltrados().length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                  </p>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PRODUTO</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">QUANTIDADE</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">VALOR (R$/KG)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PEDIU MEIA CX?</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">QUALIDADE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getProdutosFiltrados().map((produto, index) => renderLinhaProduto(produto, index))}
                  </tbody>
                </table>
              </div>

              {/* Botões de Ação - Sticky no rodapé */}
              <div className="sticky bottom-0 mt-6 pt-4 pb-2 bg-white border-t border-gray-200 z-10 flex justify-between items-center">
                {/* Botão EXPORTAR PARA IMPRESSÃO - visível apenas em modo visualização */}
                {pedidoEmEdicao.visualizacao_readonly && (
                  <button
                    onClick={exportarPedidoParaImpressao}
                    className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 font-medium flex items-center space-x-2"
                  >
                    <span>🖨️</span>
                    <span>EXPORTAR PARA IMPRESSÃO</span>
                  </button>
                )}
                
                {/* Espaçador se não houver botão de exportar */}
                {!pedidoEmEdicao.visualizacao_readonly && <div></div>}
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowEditor(false);
                      setPedidoEmEdicao(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {pedidoEmEdicao.visualizacao_readonly ? 'Fechar' : 'Cancelar'}
                  </button>
                  {/* Botão FINALIZAR apenas se não estiver em modo visualização */}
                  {!pedidoEmEdicao.visualizacao_readonly && (
                    <button
                      onClick={handleFinalizar}
                      disabled={loading || produtosEditor.filter(p => p.quantidade && parseFloat(p.quantidade) > 0).length === 0}
                      className={`px-6 py-2 rounded text-white font-medium ${
                        loading || produtosEditor.filter(p => p.quantidade && parseFloat(p.quantidade) > 0).length === 0
                          ? 'bg-gray-300 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {loading ? 'Finalizando...' : 'FINALIZAR'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Pedidos */}
      <div style={{ display: showEditor ? 'none' : 'block' }}>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Pedidos</h3>
              <p className="text-sm text-gray-600">
                {usuario.tipo_usuario === 'Gerente' 
                  ? 'Crie e gerencie seus pedidos' 
                  : 'Gerencie os pedidos das filiais'}
              </p>
            </div>
            {/* Botão "+ Novo Pedido" visível APENAS para GERENTE */}
            {usuario.tipo_usuario === 'Gerente' && (
              <button
                onClick={handleNovoPedido}
                className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
              >
                <span>➕</span>
                <span>Novo Pedido</span>
              </button>
            )}
          </div>

          {pedidosLocal.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📝</div>
              <p>Nenhum pedido criado ainda.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Agrupar pedidos por filial */}
              {Object.entries(
                pedidosLocal.reduce((grupos, pedido) => {
                  const filial = pedido.filial || 'Sem Filial';
                  if (!grupos[filial]) grupos[filial] = [];
                  grupos[filial].push(pedido);
                  return grupos;
                }, {})
              ).map(([filial, pedidosFilial]) => (
                <div key={filial} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {/* Cabeçalho da Filial */}
                  <div className="mb-4 pb-3 border-b border-gray-300">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <span className="mr-2">🏢</span>
                      {filial}
                      <span className="ml-3 text-sm font-normal text-gray-600">
                        ({pedidosFilial.length} {pedidosFilial.length === 1 ? 'pedido' : 'pedidos'})
                      </span>
                    </h3>
                  </div>
                  
                  {/* Cards de Pedidos da Filial */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pedidosFilial.map((pedido) => (
                <div key={pedido.id} className="bg-white border rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-lg font-semibold">{pedido.filial}</h4>
                      <p className="text-sm text-gray-600">Pedido #{pedido.numero}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      pedido.finalizado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {pedido.finalizado ? 'Finalizado' : 'Aberto'}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 mb-4">
                    <p>Produtos: {pedido.produtos?.length || 0}</p>
                    <p>Criado: {new Date(pedido.criado_em).toLocaleDateString('pt-BR')}</p>
                  </div>

                  <div className="flex space-x-2">
                    {/* VISUALIZAR - Aparece apenas quando pedido está finalizado */}
                    {pedido.finalizado && (
                      <button
                        onClick={() => handleVisualizarPedido(pedido)}
                        className="flex-1 px-3 py-2 rounded text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200"
                      >
                        Visualizar
                      </button>
                    )}
                    
                    {/* EDITAR - Para COMPRADOR: sempre visível | Para GERENTE: apenas se não finalizado */}
                    {(temPermissaoComprador() || !pedido.finalizado) && (
                      <button
                        onClick={() => handleEditarPedido(pedido)}
                        className="flex-1 px-3 py-2 rounded text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Editar
                      </button>
                    )}
                    
                    {/* EXCLUIR - Sempre disponível para ambos */}
                    <button
                      onClick={() => handleExcluirPedido(pedido)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-medium"
                    >
                      Excluir
                    </button>
                  </div>

                  {/* Mensagem informativa apenas para GERENTE com pedido finalizado */}
                  {usuario.tipo_usuario === 'Gerente' && pedido.finalizado && (
                    <p className="mt-2 text-xs text-gray-500 italic">
                      Pedido finalizado - somente visualização
                    </p>
                  )}
                </div>
              ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </div>
  );
};

// Separacao Tab Component
const SeparacaoTab = ({ separacoes = [], pedidos, onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSeparacao, setSelectedSeparacao] = useState(null);
  const [nomeSeparacao, setNomeSeparacao] = useState('');
  const [pedidosSelecionados, setPedidosSelecionados] = useState([]);
  const [produtosConsolidados, setProdutosConsolidados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ordenacaoFornecedor, setOrdenacaoFornecedor] = useState(false);
  
  // Estados para conferência
  const [showConferenciaModal, setShowConferenciaModal] = useState(false);
  const [separacaoParaConferir, setSeparacaoParaConferir] = useState(null);
  const [itensConferencia, setItensConferencia] = useState([]);
  const [ordenacaoFornecedorConf, setOrdenacaoFornecedorConf] = useState(false);

  // Filtrar apenas pedidos finalizados
  const pedidosFinalizados = pedidos.filter(pedido => pedido.status_pedido === 'Finalizado');

  const handleAddSeparacao = () => {
    setShowAddForm(true);
    setPedidosSelecionados([]);
    setNomeSeparacao('');
  };

  const handleSelectPedido = (pedidoId) => {
    setPedidosSelecionados(prev => 
      prev.includes(pedidoId) 
        ? prev.filter(id => id !== pedidoId)
        : [...prev, pedidoId]
    );
  };

  const handleCadastrarSeparacao = async () => {
    if (!nomeSeparacao.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe um nome para a separação",
        variant: "destructive"
      });
      return;
    }

    if (pedidosSelecionados.length === 0) {
      toast({
        title: "Seleção obrigatória",
        description: "Selecione pelo menos um pedido para a separação",
        variant: "destructive"
      });
      return;
    }

    try {
      const separacaoData = {
        nome: nomeSeparacao.trim(),
        pedidos_ids: pedidosSelecionados,
        data_criacao: new Date().toISOString(),
        usuario_responsavel: usuario.nome_completo,
        status: 'Ativa'
      };

      await axios.post('/separacoes', separacaoData);
      
      toast({
        title: "Separação cadastrada com sucesso!",
        description: `A separação "${nomeSeparacao}" foi criada com ${pedidosSelecionados.length} pedido(s)`
      });

      setShowAddForm(false);
      setNomeSeparacao('');
      setPedidosSelecionados([]);
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao cadastrar separação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSeparacao = async (separacao) => {
    const confirmed = await showConfirmDialog(
      "Excluir Separação",
      `Tem certeza que deseja excluir a separação "${separacao.nome}"? Esta ação não poderá ser desfeita.`
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/separacoes/${separacao.id}`);
      toast({
        title: "Separação excluída com sucesso!",
        description: `A separação "${separacao.nome}" foi removida`
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir separação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleVisualizarSeparacao = async (separacao) => {
    setSelectedSeparacao(separacao);
    setShowViewModal(true);
    await consolidarProdutosSeparacao(separacao);
  };

  const consolidarProdutosSeparacao = async (separacao) => {
    setLoading(true);
    try {
      // Buscar pedidos da separação
      const pedidosDaSeparacao = pedidos.filter(pedido => 
        separacao.pedidos_ids.includes(pedido.id)
      );

      // Buscar todas as cotações para identificar vencedores
      const cotacoes = await fetchAllPaginated('/cotacoes');

      // Buscar todos os produtos para ter os detalhes completos
      const todosProdutos = await fetchAllPaginated('/produtos');

      // Consolidar produtos por ID
      const produtosMap = new Map();

      pedidosDaSeparacao.forEach(pedido => {
        if (pedido.produtos && Array.isArray(pedido.produtos)) {
          pedido.produtos.forEach(produtoPedido => {
            const produtoId = produtoPedido.produto_id || produtoPedido.id;
            const quantidade = produtoPedido.quantidade || 0;

            if (produtosMap.has(produtoId)) {
              const produto = produtosMap.get(produtoId);
              produto.quantidadeTotal += quantidade;
              
              // Consolidar qualidades
              if (produtoPedido.qualidade === 'Tudo verde') {
                produto.qtd_verde += quantidade;
              } else if (produtoPedido.qualidade === 'Tudo maduro') {
                produto.qtd_maduro += quantidade;
              } else if (produtoPedido.qualidade === 'Ambos') {
                produto.qtd_verde += (produtoPedido.qtd_verde || 0);
                produto.qtd_maduro += (produtoPedido.qtd_maduro || 0);
              }
            } else {
              // Buscar detalhes do produto
              const produtoDetalhes = todosProdutos.find(p => p.id === produtoId);
              
              if (produtoDetalhes) {
                const qtdVerde = produtoPedido.qualidade === 'Tudo verde' ? quantidade : 
                                produtoPedido.qualidade === 'Ambos' ? (produtoPedido.qtd_verde || 0) : 0;
                const qtdMaduro = produtoPedido.qualidade === 'Tudo maduro' ? quantidade : 
                                 produtoPedido.qualidade === 'Ambos' ? (produtoPedido.qtd_maduro || 0) : 0;
                
                produtosMap.set(produtoId, {
                  id: produtoId,
                  nome: produtoDetalhes.nome,
                  unidade: produtoDetalhes.unidade,
                  quantidadeTotal: quantidade,
                  fornecedorVencedor: null,
                  valorVencedor: null,
                  qtd_verde: qtdVerde,
                  qtd_maduro: qtdMaduro
                });
              }
            }
          });
        }
      });

      // Identificar fornecedores vencedores das últimas cotações
      produtosMap.forEach((produto, produtoId) => {
        // Buscar cotações que contenham este produto, ordenadas por data (mais recente primeiro)
        const cotacoesComProduto = cotacoes
          .filter(cotacao => 
            cotacao.produtos_ids && 
            cotacao.produtos_ids.includes(produtoId) &&
            cotacao.status_cotacao === 'Encerrada' &&
            cotacao.vencedores
          )
          .sort((a, b) => new Date(b.data_criacao) - new Date(a.data_criacao));

        if (cotacoesComProduto.length > 0) {
          const ultimaCotacao = cotacoesComProduto[0];
          // vencedores é um objeto com produto_id como chave
          const vencedorProduto = ultimaCotacao.vencedores?.[produtoId];
          
          if (vencedorProduto) {
            produto.fornecedorVencedor = vencedorProduto.nome_fornecedor;
            produto.valorVencedor = vencedorProduto.valor_caixa / vencedorProduto.quantidade_kg; // Calcular valor por kg
          }
        }
      });

      // Converter Map para Array
      const produtosConsolidadosArray = Array.from(produtosMap.values());
      setProdutosConsolidados(produtosConsolidadosArray);

    } catch (error) {
      console.error('Erro ao consolidar produtos da separação:', error);
      toast({
        title: "Erro ao carregar produtos",
        description: "Não foi possível consolidar os produtos da separação",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getProdutosOrdenados = () => {
    if (!ordenacaoFornecedor) {
      return produtosConsolidados;
    }
    
    // Ordenar por fornecedor alfabeticamente
    return [...produtosConsolidados].sort((a, b) => {
      const fornA = a.fornecedorVencedor || 'Sem cotação';
      const fornB = b.fornecedorVencedor || 'Sem cotação';
      return fornA.localeCompare(fornB, 'pt-BR', { sensitivity: 'base' });
    });
  };

  // Função para iniciar conferência
  const handleIniciarConferencia = async (separacao) => {
    try {
      setLoading(true);
      
      // Consolidar produtos (essa função usa setProdutosConsolidados internamente)
      await consolidarProdutosSeparacao(separacao);
      
      // Aguardar um pouco para garantir que o estado foi atualizado
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Buscar produtos consolidados do estado
      // Precisamos reconstruir os produtos aqui porque setProdutosConsolidados pode não estar disponível ainda
      // Vou duplicar a lógica para ter controle total
      
      const pedidosDaSeparacao = pedidos.filter(pedido => 
        separacao.pedidos_ids.includes(pedido.id)
      );

      const cotacoes = await fetchAllPaginated('/cotacoes');

      const todosProdutos = await fetchAllPaginated('/produtos');

      const produtosMap = new Map();

      pedidosDaSeparacao.forEach(pedido => {
        if (pedido.produtos && Array.isArray(pedido.produtos)) {
          pedido.produtos.forEach(produtoPedido => {
            const produtoId = produtoPedido.produto_id || produtoPedido.id;
            const quantidade = produtoPedido.quantidade || 0;

            if (produtosMap.has(produtoId)) {
              const produto = produtosMap.get(produtoId);
              produto.quantidadeTotal += quantidade;
              
              if (produtoPedido.qualidade === 'Tudo verde') {
                produto.qtd_verde += quantidade;
              } else if (produtoPedido.qualidade === 'Tudo maduro') {
                produto.qtd_maduro += quantidade;
              } else if (produtoPedido.qualidade === 'Ambos') {
                produto.qtd_verde += (produtoPedido.qtd_verde || 0);
                produto.qtd_maduro += (produtoPedido.qtd_maduro || 0);
              }
            } else {
              const produtoDetalhes = todosProdutos.find(p => p.id === produtoId);
              
              if (produtoDetalhes) {
                const qtdVerde = produtoPedido.qualidade === 'Tudo verde' ? quantidade : 
                                produtoPedido.qualidade === 'Ambos' ? (produtoPedido.qtd_verde || 0) : 0;
                const qtdMaduro = produtoPedido.qualidade === 'Tudo maduro' ? quantidade : 
                                 produtoPedido.qualidade === 'Ambos' ? (produtoPedido.qtd_maduro || 0) : 0;
                
                produtosMap.set(produtoId, {
                  id: produtoId,
                  nome: produtoDetalhes.nome,
                  unidade: produtoDetalhes.unidade,
                  quantidadeTotal: quantidade,
                  fornecedorVencedor: null,
                  valorVencedor: null,
                  qtd_verde: qtdVerde,
                  qtd_maduro: qtdMaduro
                });
              }
            }
          });
        }
      });

      // Identificar fornecedores vencedores
      produtosMap.forEach((produto, produtoId) => {
        const cotacoesComProduto = cotacoes
          .filter(cotacao => 
            cotacao.produtos_ids && 
            cotacao.produtos_ids.includes(produtoId) &&
            cotacao.status_cotacao === 'Encerrada' &&
            cotacao.vencedores
          )
          .sort((a, b) => new Date(b.data_criacao) - new Date(a.data_criacao));

        if (cotacoesComProduto.length > 0) {
          const ultimaCotacao = cotacoesComProduto[0];
          const vencedorProduto = ultimaCotacao.vencedores?.[produtoId];
          
          if (vencedorProduto) {
            produto.fornecedorVencedor = vencedorProduto.nome_fornecedor;
          }
        }
      });

      const produtos = Array.from(produtosMap.values());
      
      // Verificar se já existe conferência salva
      const conferenciaExistente = separacao.conferencia || null;
      
      // Inicializar itens de conferência
      const itens = produtos.map(produto => {
        const itemConferido = conferenciaExistente?.itens?.find(i => i.produto_id === produto.id);
        
        return {
          produto_id: produto.id,
          nome: produto.nome,
          unidade: produto.unidade,
          quantidade: produto.quantidadeTotal,
          fornecedor: produto.fornecedorVencedor || 'Sem cotação',
          qtd_verde: produto.qtd_verde,
          qtd_maduro: produto.qtd_maduro,
          confere: itemConferido?.confere ?? null,
          quantidade_faltante: itemConferido?.quantidade_faltante || '',
          readonly: conferenciaExistente ? true : false
        };
      });
      
      setItensConferencia(itens);
      setSeparacaoParaConferir(separacao);
      setShowConferenciaModal(true);
    } catch (error) {
      toast({
        title: "Erro ao carregar produtos",
        description: error.message || "Erro ao consolidar produtos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar conferência de um item
  const handleUpdateConferencia = (index, field, value) => {
    setItensConferencia(prev => {
      const newItens = [...prev];
      newItens[index] = {
        ...newItens[index],
        [field]: value,
        // Se marcou "Sim", limpar quantidade faltante
        ...(field === 'confere' && value === true ? { quantidade_faltante: '' } : {})
      };
      return newItens;
    });
  };

  // Função para salvar conferência
  const handleSalvarConferencia = async () => {
    try {
      // CORREÇÃO 1: Permitir finalizar conferência parcial - remover validação que exigia todos conferidos
      
      // CORREÇÃO 2: Validar se itens marcados como "Não" têm quantidade faltante válida
      const itensComFaltaInvalida = itensConferencia.filter(item => {
        if (item.confere === false) {
          const qtdFaltante = parseFloat(item.quantidade_faltante);
          const qtdTotal = parseFloat(item.quantidade);
          
          // Verificar se não informou quantidade
          if (!item.quantidade_faltante || qtdFaltante <= 0) {
            return true; // Inválido - precisa informar quantidade
          }
          
          // Verificar se quantidade faltante é maior que total
          if (qtdFaltante > qtdTotal) {
            return true; // Inválido - não pode ser maior que total
          }
        }
        return false;
      });
      
      if (itensComFaltaInvalida.length > 0) {
        // Identificar o tipo de erro
        const semQuantidade = itensComFaltaInvalida.filter(item => 
          !item.quantidade_faltante || parseFloat(item.quantidade_faltante) <= 0
        );
        const quantidadeMaior = itensComFaltaInvalida.filter(item => 
          item.quantidade_faltante && parseFloat(item.quantidade_faltante) > parseFloat(item.quantidade)
        );
        
        let mensagemErro = "";
        if (semQuantidade.length > 0 && quantidadeMaior.length > 0) {
          mensagemErro = "Alguns produtos têm quantidade faltante inválida: alguns não foram informados e outros têm valor maior que a quantidade da separação";
        } else if (semQuantidade.length > 0) {
          mensagemErro = "Produtos marcados como 'Não' devem ter quantidade faltante informada";
        } else if (quantidadeMaior.length > 0) {
          const produtosProblema = quantidadeMaior.map(item => 
            `${item.nome}: faltante ${item.quantidade_faltante} > total ${item.quantidade}`
          ).join(', ');
          mensagemErro = `Quantidade faltante não pode ser maior que a quantidade total. Problemas: ${produtosProblema}`;
        }
        
        toast({
          title: "Quantidade faltante inválida",
          description: mensagemErro,
          variant: "destructive"
        });
        return;
      }
      
      // Preparar dados para salvar - incluir TODOS os itens, mesmo não conferidos
      const conferenciaData = {
        itens: itensConferencia.map(item => ({
          produto_id: item.produto_id,
          confere: item.confere, // Pode ser null, true ou false
          quantidade_faltante: item.confere === false ? parseFloat(item.quantidade_faltante) : 0
        })),
        data_conferencia: new Date().toISOString(),
        usuario_conferencia: usuario.nome_completo
      };
      
      // Salvar no backend
      await axios.put(`/separacoes/${separacaoParaConferir.id}/conferencia`, { conferencia: conferenciaData });
      
      toast({
        title: "Conferência salva com sucesso!",
        description: "Os dados foram registrados"
      });
      
      setShowConferenciaModal(false);
      
      // Atualizar dados - aguardar um pouco antes de chamar onRefresh para garantir que o backend processou
      setTimeout(() => {
        onRefresh();
      }, 500);
    } catch (error) {
      toast({
        title: "Erro ao salvar conferência",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função para ver apenas produtos com falta
  const handleVerFaltas = async (separacao) => {
    try {
      setLoading(true);
      
      // Chama handleIniciarConferencia mas depois filtra apenas faltas
      await handleIniciarConferencia(separacao);
      
      // Filtrar itens com falta: produtos marcados como "Não" (false) OU não conferidos (null)
      setItensConferencia(prev => prev.filter(item => 
        item.confere === false || item.confere === null
      ));
      
    } catch (error) {
      toast({
        title: "Erro ao carregar faltas",
        description: error.message || "Erro ao consolidar produtos",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  // Função para editar conferência existente
  const handleEditarConferencia = async (separacao) => {
    try {
      // Chama handleIniciarConferencia mas define readonly como false
      await handleIniciarConferencia(separacao);
      
      // Desabilita readonly para permitir edição
      setItensConferencia(prev => prev.map(item => ({ ...item, readonly: false })));
      
    } catch (error) {
      toast({
        title: "Erro ao carregar conferência",
        description: error.message || "Erro ao consolidar produtos",
        variant: "destructive"
      });
    }
  };



  // Formulário de adicionar separação
  if (showAddForm) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setShowAddForm(false)}
            className="flex items-center space-x-2 text-green-700 hover:text-green-800 mb-4"
          >
            <span>←</span>
            <span>Voltar</span>
          </button>
          <h3 className="text-xl font-semibold text-gray-900">Nova Separação de Entrega</h3>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da Separação *
            </label>
            <input
              type="text"
              value={nomeSeparacao}
              onChange={(e) => setNomeSeparacao(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              placeholder="Ex: Separação Centro-Norte"
              required
            />
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Selecionar Pedidos Finalizados ({pedidosFinalizados.length} disponíveis)
            </h4>
            
            {pedidosFinalizados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📋</div>
                <p>Nenhum pedido finalizado disponível para separação</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {pedidosFinalizados.map((pedido) => (
                  <div
                    key={pedido.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      pedidosSelecionados.includes(pedido.id)
                        ? 'border-green-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleSelectPedido(pedido.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{pedido.numero}</h5>
                      <input
                        type="checkbox"
                        checked={pedidosSelecionados.includes(pedido.id)}
                        onChange={() => handleSelectPedido(pedido.id)}
                        className="w-4 h-4 text-green-700"
                      />
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Filial:</strong> {pedido.filial}</p>
                      <p><strong>Data:</strong> {pedido.criado_em ? new Date(pedido.criado_em).toLocaleDateString('pt-BR') : 'Não informada'}</p>
                      <p><strong>Produtos:</strong> {pedido.produtos?.length || 0} itens</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleCadastrarSeparacao}
              disabled={!nomeSeparacao.trim() || pedidosSelecionados.length === 0}
              className={`px-6 py-2 rounded-lg font-medium ${
                nomeSeparacao.trim() && pedidosSelecionados.length > 0
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Cadastrar Separação
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modal de visualização
  // Função para exportar/imprimir separação
  const handleExportarSeparacao = () => {
    const printWindow = window.open('', '_blank');
    const dataFormatada = selectedSeparacao.data_criacao 
      ? new Date(selectedSeparacao.data_criacao).toLocaleDateString('pt-BR')
      : 'Não informada';
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Separação - ${selectedSeparacao.nome}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            font-size: 12pt;
          }
          .header {
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 24pt;
            margin-bottom: 10px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-bottom: 20px;
          }
          .info-item {
            padding: 5px 0;
          }
          .info-item strong {
            font-weight: bold;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #f3f4f6;
            border: 1px solid #d1d5db;
            padding: 12px;
            text-align: left;
            font-weight: bold;
            font-size: 11pt;
          }
          td {
            border: 1px solid #d1d5db;
            padding: 10px;
            font-size: 11pt;
          }
          tbody tr:nth-child(even) {
            background-color: #f9fafb;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #d1d5db;
            font-size: 10pt;
            color: #6b7280;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SEPARAÇÃO DE ENTREGA</h1>
          <p style="font-size: 14pt; color: #4b5563;">${selectedSeparacao.nome}</p>
        </div>
        
        <div class="info-grid">
          <div class="info-item">
            <strong>Data:</strong> ${dataFormatada}
          </div>
          <div class="info-item">
            <strong>Responsável:</strong> ${selectedSeparacao.usuario_responsavel}
          </div>
          <div class="info-item">
            <strong>N° de Pedidos:</strong> ${selectedSeparacao.pedidos_ids?.length || 0}
          </div>
          <div class="info-item">
            <strong>Status:</strong> ${selectedSeparacao.status_separacao}
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>PRODUTO</th>
              <th>QUANTIDADE</th>
              <th>FORNECEDOR</th>
            </tr>
          </thead>
          <tbody>
            ${getProdutosOrdenados().map(produto => {
              let qualidadeInfo = '';
              if (produto.qtd_verde > 0 && produto.qtd_maduro > 0) {
                qualidadeInfo = `<br/><span style="font-size: 9pt; color: #059669;">VERDE: ${produto.qtd_verde.toFixed(2)}</span> • <span style="font-size: 9pt; color: #c2410c;">MADURO: ${produto.qtd_maduro.toFixed(2)}</span>`;
              } else if (produto.qtd_verde > 0) {
                qualidadeInfo = `<br/><span style="font-size: 9pt; color: #059669;">TUDO VERDE</span>`;
              } else if (produto.qtd_maduro > 0) {
                qualidadeInfo = `<br/><span style="font-size: 9pt; color: #c2410c;">TUDO MADURO</span>`;
              }
              return `
              <tr>
                <td>${produto.nome}</td>
                <td>${produto.quantidadeTotal} ${produto.unidade}${qualidadeInfo}</td>
                <td>${produto.fornecedorVencedor || 'Sem cotação'}</td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p><strong>Total de produtos diferentes:</strong> ${produtosConsolidados.length}</p>
          <p style="margin-top: 10px;">
            <em>Fornecedores baseados na última cotação encerrada de cada produto</em>
          </p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (showViewModal && selectedSeparacao) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowViewModal(false)}>
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedSeparacao.nome}
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Data:</span>
                  <p>{selectedSeparacao.data_criacao ? new Date(selectedSeparacao.data_criacao).toLocaleDateString('pt-BR') : 'Não informada'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Responsável:</span>
                  <p>{selectedSeparacao.usuario_responsavel}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Pedidos:</span>
                  <p>{selectedSeparacao.pedidos_ids?.length || 0}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Status:</span>
                  <p className="text-green-600">{selectedSeparacao.status_separacao}</p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-medium text-gray-900">Produtos da Separação</h4>
                {produtosConsolidados.length > 0 && (
                  <button
                    onClick={handleExportarSeparacao}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
                  >
                    <span>🖨️</span>
                    <span>Exportar para Impressão</span>
                  </button>
                )}
              </div>
              {produtosConsolidados.length > 0 && (
                <div className="flex items-center">
                  <button
                    onClick={() => setOrdenacaoFornecedor(!ordenacaoFornecedor)}
                    className={`px-4 py-2 rounded flex items-center gap-2 font-medium transition-colors ${
                      ordenacaoFornecedor 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{ordenacaoFornecedor ? '✓' : '○'}</span>
                    <span>Ordenar por Fornecedor</span>
                  </button>
                </div>
              )}
            </div>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="text-gray-500">Carregando produtos...</div>
              </div>
            ) : produtosConsolidados.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Total de produtos diferentes:</strong> {produtosConsolidados.length}
                  </p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                          PRODUTO
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                          QUANTIDADE
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          FORNECEDOR
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getProdutosOrdenados().map((produto, index) => (
                        <tr key={produto.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                            {produto.nome}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
                            <div>
                              <span className="font-semibold">{produto.quantidadeTotal}</span> {produto.unidade}
                            </div>
                            {produto.qtd_verde > 0 && produto.qtd_maduro > 0 ? (
                              <div className="text-xs text-gray-600 mt-1">
                                <span className="text-green-700">VERDE: {produto.qtd_verde.toFixed(2)}</span>
                                {' • '}
                                <span className="text-orange-700">MADURO: {produto.qtd_maduro.toFixed(2)}</span>
                              </div>
                            ) : produto.qtd_verde > 0 ? (
                              <div className="text-xs text-green-700 mt-1">TUDO VERDE</div>
                            ) : produto.qtd_maduro > 0 ? (
                              <div className="text-xs text-orange-700 mt-1">TUDO MADURO</div>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`${produto.fornecedorVencedor ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                              {produto.fornecedorVencedor || 'Sem cotação'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-3 mt-4">
                  <div className="flex items-start space-x-2">
                    <div className="text-blue-600 text-sm">ℹ️</div>
                    <div className="text-sm text-blue-700">
                      <p>
                        <strong>Fornecedor:</strong> Baseado na última cotação encerrada de cada produto. "Sem cotação" indica que o produto ainda não foi cotado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className="text-gray-400 text-4xl mb-4">📦</div>
                <p className="text-gray-600">Nenhum produto encontrado nos pedidos desta separação</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Modal de Conferência
  if (showConferenciaModal && separacaoParaConferir) {
    const todosConferidos = itensConferencia.every(item => item.confere !== null);
    const temFaltas = itensConferencia.some(item => item.confere === false || item.confere === null); // Incluir não conferidos
    
    // Função para ordenar por fornecedor
    const getItensOrdenados = () => {
      if (ordenacaoFornecedorConf) {
        return [...itensConferencia].sort((a, b) => {
          const fornA = a.fornecedor || 'Sem cotação';
          const fornB = b.fornecedor || 'Sem cotação';
          return fornA.localeCompare(fornB);
        });
      }
      return itensConferencia;
    };
    
    const itensFiltrados = getItensOrdenados();
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-green-600 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">
                {itensConferencia[0]?.readonly && temFaltas ? 'Produtos com Falta ou Não Conferidos' : 'Conferência de Separação'}
              </h3>
              <button
                onClick={() => {
                  setShowConferenciaModal(false);
                  setSeparacaoParaConferir(null);
                  setItensConferencia([]);
                  setOrdenacaoFornecedorConf(false);
                }}
                className="text-white hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <p className="text-green-100 text-sm mt-1">{separacaoParaConferir.nome}</p>
          </div>
          
          {/* Botão Ordenar */}
          <div className="px-6 py-3 border-b bg-gray-50">
            <button
              onClick={() => setOrdenacaoFornecedorConf(!ordenacaoFornecedorConf)}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                ordenacaoFornecedorConf
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {ordenacaoFornecedorConf ? '✓ Ordenado por Fornecedor (A-Z)' : 'Ordenar por Fornecedor (A-Z)'}
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {itensFiltrados.length === 0 ? (
              <div className="bg-green-50 rounded-lg p-8 text-center">
                <div className="text-green-600 text-5xl mb-4">🎉</div>
                <p className="text-xl font-semibold text-green-800 mb-2">
                  Parabéns!!
                </p>
                <p className="text-green-700">
                  Todos os produtos conferidos conforme a separação.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {!itensConferencia[0]?.readonly && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          CONFERE?
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        PRODUTO
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        QUANTIDADE
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        FORNECEDOR
                      </th>
                      {itensConferencia.some(item => item.confere === false) && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          QTD FALTANTE
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {itensFiltrados.map((item, index) => {
                      // Encontrar índice real no array completo
                      const realIndex = itensConferencia.findIndex(i => i.produto_id === item.produto_id);
                      
                      return (
                        <tr key={item.produto_id} className={item.confere === true ? 'bg-green-50' : ''}>
                          {!item.readonly && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex space-x-4">
                                <label className="flex items-center space-x-1 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`confere-${item.produto_id}`}
                                    checked={item.confere === true}
                                    onChange={() => handleUpdateConferencia(realIndex, 'confere', true)}
                                    className="form-radio text-green-600"
                                  />
                                  <span className="text-sm text-gray-700">Sim</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`confere-${item.produto_id}`}
                                    checked={item.confere === false}
                                    onChange={() => handleUpdateConferencia(realIndex, 'confere', false)}
                                    className="form-radio text-red-600"
                                  />
                                  <span className="text-sm text-gray-700">Não</span>
                                </label>
                              </div>
                            </td>
                          )}
                          <td className="px-6 py-4">
                            <span className={item.confere === true ? 'line-through text-gray-500' : 'text-gray-900'}>
                              {item.nome}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              {item.quantidade} {item.unidade}
                            </div>
                            {item.qtd_verde > 0 && item.qtd_maduro > 0 ? (
                              <div className="text-xs text-gray-600 mt-1">
                                <span className="text-green-700">VERDE: {item.qtd_verde.toFixed(2)}</span>
                                {' • '}
                                <span className="text-orange-700">MADURO: {item.qtd_maduro.toFixed(2)}</span>
                              </div>
                            ) : item.qtd_verde > 0 ? (
                              <div className="text-xs text-green-700 mt-1">TUDO VERDE</div>
                            ) : item.qtd_maduro > 0 ? (
                              <div className="text-xs text-orange-700 mt-1">TUDO MADURO</div>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.fornecedor}
                          </td>
                          {(item.confere === false || itensConferencia.some(i => i.confere === false)) && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.confere === false ? (
                                <div>
                                  <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    max={item.quantidade}
                                    value={item.quantidade_faltante}
                                    onChange={(e) => handleUpdateConferencia(realIndex, 'quantidade_faltante', e.target.value)}
                                    disabled={item.readonly}
                                    className={`w-24 px-2 py-1 border rounded text-sm ${
                                      item.quantidade_faltante && parseFloat(item.quantidade_faltante) > parseFloat(item.quantidade)
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-300'
                                    }`}
                                    placeholder="0"
                                  />
                                  {item.quantidade_faltante && parseFloat(item.quantidade_faltante) > parseFloat(item.quantidade) && (
                                    <p className="text-xs text-red-600 mt-1">
                                      Máx: {item.quantidade}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Footer com botões */}
          {!itensConferencia[0]?.readonly && (
            <div className="border-t px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConferenciaModal(false);
                  setSeparacaoParaConferir(null);
                  setItensConferencia([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarConferencia}
                className="px-4 py-2 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700"
              >
                Finalizar Conferência {!todosConferidos && '(Parcial)'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }


  // Vista principal
  return (
    <div>
      <div className="mb-6">
        <button
          onClick={handleAddSeparacao}
          className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center space-x-2"
          data-testid="add-separacao-button"
        >
          <span>+</span>
          <span>Adicionar Separação</span>
        </button>
      </div>
      
      {separacoes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🚚</div>
          <p>Nenhuma separação de entrega criada ainda.</p>
          <p className="text-sm mt-2">Clique em "Adicionar Separação" para começar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {separacoes.map((separacao) => (
            <div key={separacao.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">{separacao.nome}</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Data:</strong> {separacao.data_criacao ? new Date(separacao.data_criacao).toLocaleDateString('pt-BR') : 'Não informada'}</p>
                <p><strong>Pedidos:</strong> {separacao.pedidos_ids?.length || 0}</p>
                <p><strong>Responsável:</strong> {separacao.usuario_responsavel}</p>
                <p><strong>Status:</strong> 
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                    separacao.status_separacao === 'Ativa' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {separacao.status_separacao}
                  </span>
                </p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button 
                  onClick={() => handleVisualizarSeparacao(separacao)}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                >
                  Visualizar
                </button>
                
                {/* Botões de conferência - apenas para MOTORISTA e COMPRADOR */}
                {(usuario.tipo_usuario === 'Motorista' || temPermissaoComprador()) && (
                  <>
                    {!separacao.conferencia ? (
                      <button 
                        onClick={() => handleIniciarConferencia(separacao)}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                      >
                        Conferir
                      </button>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleVerFaltas(separacao)}
                          className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200"
                        >
                          Ver Faltas
                        </button>
                        <button 
                          onClick={() => handleEditarConferencia(separacao)}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200"
                        >
                          Editar Conferência
                        </button>
                      </>
                    )}
                  </>
                )}
                
                <button 
                  onClick={() => handleDeleteSeparacao(separacao)}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Recebimento Tab Component
const RecebimentoTab = ({ recebimentos = [], pedidos, produtos = [], onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showConferencia, setShowConferencia] = useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [itensConferencia, setItensConferencia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false); // Novo estado para controlar modo de edição
  const [recebimentoId, setRecebimentoId] = useState(null); // ID do recebimento sendo editado
  const [searchText, setSearchText] = useState(''); // Estado para pesquisa de produtos

  // Filtrar pedidos por filial se for Gerente
  const pedidosFiltrados = usuario.tipo_usuario === 'Gerente' 
    ? pedidos.filter(p => p.filial === usuario.filial)
    : pedidos;

  // Agrupar por filial para Comprador e Administrador
  const pedidosPorFilial = usuario.tipo_usuario === 'Gerente' 
    ? { [usuario.filial]: pedidosFiltrados }
    : pedidos.reduce((acc, pedido) => {
        const filial = pedido.filial || 'Sem filial';
        if (!acc[filial]) acc[filial] = [];
        acc[filial].push(pedido);
        return acc;
      }, {});

  // Função para iniciar conferência
  const iniciarConferencia = async (pedido, modoEditar = false) => {
    setLoading(true);
    
    try {
      // Verificar se pedido já foi conferido
      const recebimentoExistente = recebimentos.find(r => r.pedido_id === pedido.id);
      
      if (recebimentoExistente) {
        // Buscar cotações para atualizar peso por caixa
        let cotacoes = [];
        try {
          cotacoes = await fetchAllPaginated('/cotacoes');
        } catch (error) {
          console.warn('Erro ao buscar cotações, continuando sem atualização:', error);
          // Continuar sem cotações - usará peso do recebimento existente ou do produto
        }
        
        // Combinar dados do recebimento com dados originais do pedido
        const itensConferencia = recebimentoExistente.itens.map(itemRecebimento => {
          // Buscar dados originais do produto no pedido
          const produtoOriginal = pedido.produtos?.find(p => 
            (p.produto_id || p.id) === itemRecebimento.produto_id
          );
          
          // Atualizar peso_por_caixa da cotação ou produto
          let pesoPorCaixaAtualizado = itemRecebimento.peso_por_caixa || 0;
          
          // Buscar peso por caixa atualizado da cotação
          const cotacoesComProduto = cotacoes
            .filter(cotacao => 
              cotacao.produtos_ids && 
              cotacao.produtos_ids.includes(itemRecebimento.produto_id) &&
              cotacao.status_cotacao === 'Encerrada' &&
              cotacao.vencedores
            )
            .sort((a, b) => {
              const dataA = new Date(a.criado_em || a.data_criacao || 0);
              const dataB = new Date(b.criado_em || b.data_criacao || 0);
              return dataB - dataA;
            });

          if (cotacoesComProduto.length > 0) {
            const ultimaCotacao = cotacoesComProduto[0];
            const vencedorProduto = ultimaCotacao.vencedores?.[itemRecebimento.produto_id];
            
            if (vencedorProduto && vencedorProduto.quantidade_kg) {
              pesoPorCaixaAtualizado = parseFloat(vencedorProduto.quantidade_kg) || 0;
            }
          }
          
          // Se não houver cotação, buscar do cadastro do produto
          if (pesoPorCaixaAtualizado === 0) {
            const produtoCadastrado = produtos.find(p => p.id === itemRecebimento.produto_id);
            if (produtoCadastrado) {
              // Para produtos UN sem cotação, peso esperado = quantidade
              if (produtoCadastrado.unidade === 'UN') {
                pesoPorCaixaAtualizado = 1; // 1 UN = 1 kg para cálculo
              } else if (produtoCadastrado.peso_cx) {
                pesoPorCaixaAtualizado = parseFloat(produtoCadastrado.peso_cx) || 0;
              }
            }
          }
          
          // Recalcular divergência com peso atualizado
          const pesoRecebido = parseFloat(itemRecebimento.peso) || 0;
          const quantidadePedido = produtoOriginal?.quantidade || itemRecebimento.quantidade_pedido || 0;
          const pesoEsperado = quantidadePedido * pesoPorCaixaAtualizado;
          const divergenciaAtualizada = pesoRecebido - pesoEsperado;
          
          return {
            ...itemRecebimento,
            readonly: !modoEditar, // Se modoEditar=true, readonly=false
            quantidade_pedido: quantidadePedido,
            nome_produto: itemRecebimento.nome_produto || produtoOriginal?.nome || produtoOriginal?.nome_produto || 'N/A',
            divergencia: divergenciaAtualizada,
            peso_por_caixa: pesoPorCaixaAtualizado,
            quantidade_faltante: itemRecebimento.quantidade_faltante || 0,
            // Manter confere baseado nos dados existentes
            confere: itemRecebimento.confere
          };
        });
        
        setItensConferencia(itensConferencia);
        setModoEdicao(modoEditar);
        setRecebimentoId(recebimentoExistente.id); // Salvar ID para edição
      } else {
        // Inicializar itens para conferência
        if (!pedido.produtos || pedido.produtos.length === 0) {
          toast({
            title: "Pedido sem produtos",
            description: "Este pedido não possui produtos para conferir",
            variant: "destructive"
          });
          return;
        }

        // Buscar cotações para obter peso por caixa dos fornecedores vencedores
        let cotacoes = [];
        try {
          cotacoes = await fetchAllPaginated('/cotacoes');
        } catch (error) {
          console.warn('Erro ao buscar cotações, continuando sem cotações:', error);
          // Continuar sem cotações - usará peso do cadastro do produto
        }

        const itensIniciais = pedido.produtos.map(produto => {
          const produtoId = produto.produto_id || produto.id;
          
          // Buscar peso por caixa do fornecedor vencedor na última cotação
          let pesoPorCaixa = 0;
          
          // Encontrar cotações que contenham este produto, ordenadas por data (mais recente primeiro)
          const cotacoesComProduto = cotacoes
            .filter(cotacao => 
              cotacao.produtos_ids && 
              cotacao.produtos_ids.includes(produtoId) &&
              cotacao.status_cotacao === 'Encerrada' &&
              cotacao.vencedores
            )
            .sort((a, b) => {
              const dataA = new Date(a.criado_em || a.data_criacao || 0);
              const dataB = new Date(b.criado_em || b.data_criacao || 0);
              return dataB - dataA;
            });

          if (cotacoesComProduto.length > 0) {
            const ultimaCotacao = cotacoesComProduto[0];
            const vencedorProduto = ultimaCotacao.vencedores?.[produtoId];
            
            if (vencedorProduto && vencedorProduto.quantidade_kg) {
              pesoPorCaixa = parseFloat(vencedorProduto.quantidade_kg) || 0;
            }
          }
          
          // Se não houver cotação, buscar peso_cx do cadastro do produto
          if (pesoPorCaixa === 0) {
            const produtoCadastrado = produtos.find(p => p.id === produtoId);
            if (produtoCadastrado) {
              // Para produtos UN sem cotação, peso esperado = quantidade
              if (produtoCadastrado.unidade === 'UN') {
                pesoPorCaixa = 1; // 1 UN = 1 kg para cálculo
              } else if (produtoCadastrado.peso_cx) {
                pesoPorCaixa = parseFloat(produtoCadastrado.peso_cx) || 0;
              }
            }
          }

          return {
            produto_id: produtoId,
            nome_produto: produto.nome || produto.nome_produto,
            quantidade_pedido: produto.quantidade || 0,
            confere: true, // Padrão: confere=true (se não marcar falta, significa que confere)
            quantidade_faltante: 0,
            peso: '',
            peso_por_caixa: pesoPorCaixa,
            divergencia: 0,
            preco_final: 0
          };
        });

        setItensConferencia(itensIniciais);
      }

      setPedidoSelecionado(pedido);
      setShowConferencia(true);
      
    } catch (error) {
      console.error('Erro ao iniciar conferência:', error);
      toast({
        title: "Erro ao carregar dados",
        description: error.message || "Não foi possível carregar os dados para conferência",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar item da conferência
  const atualizarItemConferencia = (index, campo, valor) => {
    setItensConferencia(prev => {
      const novosItens = [...prev];
      
      if (campo === 'confere') {
        novosItens[index] = {
          ...novosItens[index],
          confere: valor,
          quantidade_faltante: valor ? 0 : novosItens[index].quantidade_faltante
        };
      } else {
        novosItens[index] = {
          ...novosItens[index],
          [campo]: valor
        };
      }

      // Calcular divergência em tempo real quando peso é alterado
      if (campo === 'peso' && valor) {
        const item = novosItens[index];
        const pesoRecebido = parseFloat(valor) || 0;
        const quantidadePedido = parseFloat(item.quantidade_pedido) || 0;
        const pesoPorCaixa = parseFloat(item.peso_por_caixa) || 0;
        
        // Fórmula: Divergência = Peso Recebido - (Quantidade Pedido × Peso por Caixa)
        const pesoEsperado = quantidadePedido * pesoPorCaixa;
        const divergencia = pesoRecebido - pesoEsperado;
        
        novosItens[index] = {
          ...novosItens[index],
          divergencia: divergencia
        };
      }
      
      return novosItens;
    });
  };

  // Validação para habilitar botão finalizar
  const podeFinalizarConferencia = () => {
    return itensConferencia.every(item => {
      const quantidadePedido = parseFloat(item.quantidade_pedido) || 0;
      const quantidadeFaltante = parseFloat(item.quantidade_faltante) || 0;
      const peso = parseFloat(item.peso) || 0;
      
      // Se não confere (falta marcada)
      if (!item.confere) {
        // Verificar se quantidade faltante foi informada
        if (quantidadeFaltante <= 0) return false;
        
        // NOVA REGRA: Se produto NÃO CHEGOU (faltante >= total), peso NÃO é obrigatório
        // Usando >= ao invés de === para evitar problemas de comparação float
        if (quantidadeFaltante >= quantidadePedido) {
          return true; // Permite finalizar sem peso quando produto não chegou
        }
        
        // Se chegou parcialmente (faltante < total), peso É obrigatório
        if (peso <= 0) return false; // Exige peso em entrega parcial
        
        return true;
      }
      
      // Se confere (produto OK), peso é obrigatório
      if (peso <= 0) return false;
      
      return true;
    });
  };

  // Função para finalizar conferência (criar ou editar)
  const finalizarConferencia = async () => {
    if (!podeFinalizarConferencia()) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios antes de finalizar",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const itensData = itensConferencia.map(item => ({
        produto_id: item.produto_id,
        nome_produto: item.nome_produto,
        quantidade: item.confere ? item.quantidade_pedido : (item.quantidade_pedido - (parseFloat(item.quantidade_faltante) || 0)),
        peso: parseFloat(item.peso),
        confere: item.confere,
        quantidade_faltante: item.confere ? 0 : (parseFloat(item.quantidade_faltante) || 0),
        peso_por_caixa: item.peso_por_caixa || 0,
        divergencia: item.divergencia || 0,
        preco_final: item.preco_final || 0
      }));

      if (modoEdicao && recebimentoId) {
        // EDITAR conferência existente
        const recebimentoData = {
          itens: itensData
        };
        
        await axios.put(`/recebimentos/${recebimentoId}`, recebimentoData);

        toast({
          title: "Conferência atualizada!",
          description: `Pedido ${pedidoSelecionado.numero} atualizado com sucesso`
        });
      } else {
        // CRIAR nova conferência
        const recebimentoData = {
          pedido_id: pedidoSelecionado.id,
          itens: itensData
        };
        
        await axios.post('/recebimentos', recebimentoData);

        toast({
          title: "Conferência finalizada!",
          description: `Pedido ${pedidoSelecionado.numero} conferido com sucesso`
        });
      }

      // Limpar estados
      setShowConferencia(false);
      setPedidoSelecionado(null);
      setItensConferencia([]);
      setModoEdicao(false);
      setRecebimentoId(null);
      onRefresh(); // Recarregar dados

    } catch (error) {
      toast({
        title: modoEdicao ? "Erro ao atualizar conferência" : "Erro ao finalizar conferência",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar conferência para impressão
  const exportarConferenciaParaImpressao = (pedido, itens, readonly = false) => {
    const printWindow = window.open('', '_blank');
    
    // Buscar dados do recebimento se for readonly
    let conferidoPor = 'Pendente';
    let dataConferencia = 'Não finalizada';
    
    if (readonly) {
      const recebimento = recebimentos.find(r => r.pedido_id === pedido.id);
      if (recebimento) {
        conferidoPor = recebimento.conferido_por || usuario.nome_completo || 'Sistema';
        dataConferencia = recebimento.conferido_em 
          ? new Date(recebimento.conferido_em).toLocaleString('pt-BR')
          : new Date().toLocaleString('pt-BR');
      }
    }
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Conferência - Pedido ${pedido.numero}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            font-size: 11pt;
          }
          .header {
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 22pt;
            margin-bottom: 10px;
          }
          .header h2 {
            font-size: 16pt;
            color: #4b5563;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 20px;
            background-color: #f3f4f6;
            padding: 15px;
            border-radius: 5px;
          }
          .info-item {
            padding: 5px 0;
          }
          .info-item strong {
            font-weight: bold;
            display: block;
            color: #374151;
            margin-bottom: 3px;
          }
          .info-item span {
            color: #1f2937;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #e5e7eb;
            border: 1px solid #9ca3af;
            padding: 10px 8px;
            text-align: left;
            font-weight: bold;
            font-size: 10pt;
          }
          td {
            border: 1px solid #d1d5db;
            padding: 8px;
            font-size: 10pt;
          }
          tbody tr:nth-child(even) {
            background-color: #f9fafb;
          }
          .status-badge {
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 9pt;
            font-weight: 500;
          }
          .status-sim {
            background-color: #d1fae5;
            color: #065f46;
          }
          .status-nao {
            background-color: #fee2e2;
            color: #991b1b;
          }
          .divergencia-positiva {
            color: #1d4ed8;
            font-weight: 600;
          }
          .divergencia-negativa {
            color: #dc2626;
            font-weight: 600;
          }
          .divergencia-zero {
            color: #059669;
            font-weight: 600;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #d1d5db;
            font-size: 9pt;
            color: #6b7280;
          }
          .readonly-badge {
            display: inline-block;
            background-color: #dbeafe;
            color: #1e40af;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 10pt;
            margin-top: 10px;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CONFERÊNCIA DE RECEBIMENTO</h1>
          <h2>Pedido #${pedido.numero}</h2>
          ${readonly ? '<div class="readonly-badge">✓ Conferência Finalizada</div>' : ''}
        </div>
        
        <div class="info-grid">
          <div class="info-item">
            <strong>Filial:</strong>
            <span>${pedido.filial || 'Não informada'}</span>
          </div>
          <div class="info-item">
            <strong>Data do Pedido:</strong>
            <span>${new Date(pedido.criado_em || Date.now()).toLocaleString('pt-BR')}</span>
          </div>
          <div class="info-item">
            <strong>Responsável pela Conferência:</strong>
            <span>${readonly ? conferidoPor : usuario.nome_completo}</span>
          </div>
          <div class="info-item">
            <strong>Data da Conferência:</strong>
            <span>${readonly ? dataConferencia : new Date().toLocaleString('pt-BR')}</span>
          </div>
          <div class="info-item">
            <strong>Status:</strong>
            <span>${readonly ? 'Conferido ✓' : 'Em conferência'}</span>
          </div>
          <div class="info-item">
            <strong>Total de Produtos:</strong>
            <span>${itens.length}</span>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 25%;">Produto</th>
              <th style="width: 12%; text-align: center;">Qtd. Pedido</th>
              <th style="width: 12%; text-align: center;">Confere?</th>
              <th style="width: 15%; text-align: center;">Qtd. Faltante</th>
              <th style="width: 12%; text-align: center;">Peso (kg)</th>
              <th style="width: 14%; text-align: center;">Divergência</th>
            </tr>
          </thead>
          <tbody>
            ${itens.map(item => {
              const confereDef = item.confere !== undefined && item.confere !== null;
              const confereTexto = confereDef ? (item.confere ? 'Sim' : 'Não') : '—';
              const confereClass = confereDef ? (item.confere ? 'status-sim' : 'status-nao') : '';
              
              const qtdFaltante = parseFloat(item.quantidade_faltante) || 0;
              const qtdFaltanteTexto = (!item.confere && qtdFaltante > 0) ? qtdFaltante.toString() : '—';
              
              const peso = parseFloat(item.peso) || 0;
              const pesoTexto = peso > 0 ? peso.toFixed(2) : '—';
              
              const divergencia = parseFloat(item.divergencia) || 0;
              let divergenciaClass = 'divergencia-zero';
              let divergenciaTexto = '—';
              
              const pesoPorCaixa = parseFloat(item.peso_por_caixa) || 0;
              if (pesoPorCaixa > 0) {
                if (divergencia > 0) {
                  divergenciaClass = 'divergencia-positiva';
                  divergenciaTexto = `+${divergencia.toFixed(2)} kg (excesso)`;
                } else if (divergencia < 0) {
                  divergenciaClass = 'divergencia-negativa';
                  divergenciaTexto = `${divergencia.toFixed(2)} kg (falta)`;
                } else {
                  divergenciaTexto = '0.00 kg (conforme)';
                }
              }
              
              return `
                <tr>
                  <td><strong>${item.nome_produto}</strong></td>
                  <td style="text-align: center;"><strong>${item.quantidade_pedido}</strong></td>
                  <td style="text-align: center;">
                    ${confereClass ? `<span class="status-badge ${confereClass}">${confereTexto}</span>` : confereTexto}
                  </td>
                  <td style="text-align: center;">${qtdFaltanteTexto}</td>
                  <td style="text-align: center;">${pesoTexto}</td>
                  <td style="text-align: center;">
                    <span class="${divergenciaClass}">${divergenciaTexto}</span>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p><strong>Legenda:</strong></p>
          <p style="margin-top: 5px;">
            • <strong>Divergência:</strong> Diferença entre o peso recebido e o peso esperado (Quantidade Pedido × Peso por Caixa)<br/>
            • <strong>Confere?:</strong> Indica se a quantidade recebida está correta<br/>
            • <strong>Qtd. Faltante:</strong> Quantidade que não foi entregue (quando "Confere?" = Não)<br/>
            • Campos com "—" indicam dados não preenchidos ou não aplicáveis
          </p>
          <p style="margin-top: 15px; font-style: italic;">
            Documento gerado em ${new Date().toLocaleString('pt-BR')}
          </p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Interface de conferência
  if (showConferencia && pedidoSelecionado) {
    const isReadonly = itensConferencia.some(item => item.readonly);

    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowConferencia(false);
              setPedidoSelecionado(null);
              setItensConferencia([]);
              setSearchText('');
            }}
            className="flex items-center space-x-2 text-green-700 hover:text-green-800 mb-4"
          >
            <span>←</span>
            <span>Voltar</span>
          </button>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {isReadonly 
                ? 'Conferência Realizada' 
                : modoEdicao 
                  ? '✏️ Editando Conferência' 
                  : 'Conferir Pedido'
              } - #{pedidoSelecionado.numero}
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
              <div>
                <span className="font-medium text-gray-700">Filial:</span>
                <p className="text-gray-900">{pedidoSelecionado.filial}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Data:</span>
                <p className="text-gray-900">{new Date(pedidoSelecionado.criado_em).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <p className="text-gray-900">{pedidoSelecionado.status_pedido}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Produtos:</span>
                <p className="text-gray-900">{itensConferencia.length}</p>
              </div>
            </div>

            {isReadonly && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="text-blue-600">✅</div>
                    <div className="text-sm text-blue-700 flex-1">
                      <p className="font-medium mb-2">Conferência Finalizada</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="font-medium">Conferido por:</span>
                          <p className="text-blue-800">{pedidoSelecionado.conferido_por || 'Sistema'}</p>
                        </div>
                        <div>
                          <span className="font-medium">Data da conferência:</span>
                          <p className="text-blue-800">
                            {pedidoSelecionado.conferido_em 
                              ? new Date(pedidoSelecionado.conferido_em).toLocaleString()
                              : 'Não informada'
                            }
                          </p>
                        </div>
                        <div>
                          <span className="font-medium">Status:</span>
                          <p className="text-blue-800 font-semibold">Conferido ✓</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Botão para habilitar edição */}
                  <button
                    onClick={() => {
                      // Reabrir conferência em modo edição
                      iniciarConferencia(pedidoSelecionado, true);
                    }}
                    className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium flex items-center space-x-2"
                  >
                    <span>✏️</span>
                    <span>Editar Conferência</span>
                  </button>
                </div>
              </div>
            )}

            {/* Botão de exportação para impressão */}
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => exportarConferenciaParaImpressao(pedidoSelecionado, itensConferencia, isReadonly)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
              >
                <span>🖨️</span>
                <span>Exportar para Impressão</span>
              </button>
            </div>

            {/* Barra de pesquisa - Sticky no topo */}
            <div className="sticky top-0 bg-white z-10 pb-4 mb-4 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pesquisar produto por nome..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                />
                <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Filtrar itens baseado na pesquisa */}
            {(() => {
              const itensFiltrados = itensConferencia.filter(item => 
                item.nome_produto.toLowerCase().includes(searchText.toLowerCase())
              );

              if (itensFiltrados.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg">Nenhum produto encontrado.</p>
                  </div>
                );
              }

              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Produto</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Quantidade Pedido</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Confere?</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Quantidade Faltante</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Peso (kg)</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Divergência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensFiltrados.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="font-medium text-gray-900">{item.nome_produto}</div>
                      </td>
                      
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <span className="font-semibold">{item.quantidade_pedido}</span>
                      </td>
                      
                      <td className="border border-gray-300 px-4 py-2">
                        {isReadonly ? (
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            item.confere ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {item.confere ? 'Confere' : 'Falta'}
                          </span>
                        ) : (
                          <div className="space-y-2">
                            {/* Botão para marcar falta */}
                            <button
                              onClick={() => {
                                // Toggle: se já está marcado como falta, volta para confere
                                atualizarItemConferencia(index, 'confere', !item.confere);
                              }}
                              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                !item.confere 
                                  ? 'bg-red-600 text-white hover:bg-red-700' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {!item.confere ? '✓ Falta Marcada' : 'Marcar Falta'}
                            </button>
                            
                            {/* Status visual quando confere=true */}
                            {item.confere && (
                              <div className="text-xs text-green-600 font-medium">
                                ✓ Confere
                              </div>
                            )}
                            
                            {/* Campo quantidade faltante (aparece só se confere = false) */}
                            {item.confere === false && (
                              <div className="mt-2">
                                <label className="block text-xs text-gray-600 mb-1">Quantidade Faltante:</label>
                                <input
                                  type="number"
                                  min="0.1"
                                  max={item.quantidade_pedido}
                                  step="0.5"
                                  value={item.quantidade_faltante}
                                  onChange={(e) => atualizarItemConferencia(index, 'quantidade_faltante', e.target.value)}
                                  className="w-full px-2 py-1 border border-red-300 rounded text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                  placeholder="Ex: 2.0"
                                  required
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {isReadonly ? (
                          <div>
                            {item.confere ? (
                              <span className="text-gray-400 text-sm">—</span>
                            ) : (
                              <span className="font-semibold text-red-600">
                                {item.quantidade_faltante || 0}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-400 text-sm">
                            {item.confere === false ? (
                              <span className="text-red-600 font-medium">
                                {item.quantidade_faltante || 0}
                              </span>
                            ) : (
                              '—'
                            )}
                          </div>
                        )}
                      </td>
                      
                      <td className="border border-gray-300 px-4 py-2">
                        {isReadonly ? (
                          <div>
                            {item.peso ? (
                              <span className="font-semibold">{item.peso} kg</span>
                            ) : (
                              <span className="text-gray-400 text-sm">Produto não chegou</span>
                            )}
                          </div>
                        ) : (
                          <div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.peso}
                              onChange={(e) => atualizarItemConferencia(index, 'peso', e.target.value)}
                              className={`w-full px-2 py-1 border rounded ${
                                !item.confere && parseFloat(item.quantidade_faltante) >= item.quantidade_pedido
                                  ? 'border-amber-300 bg-amber-50'
                                  : 'border-gray-300'
                              }`}
                              placeholder="0.00"
                            />
                            {item.peso_por_caixa > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                Esperado: {(item.quantidade_pedido * item.peso_por_caixa).toFixed(2)} kg
                              </div>
                            )}
                            {/* Dica quando produto não chegou */}
                            {!item.confere && parseFloat(item.quantidade_faltante || 0) >= item.quantidade_pedido && (
                              <div className="text-xs text-amber-600 mt-1 font-medium">
                                ℹ️ Produto não chegou - peso opcional
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {item.peso_por_caixa === 0 ? (
                          <div className="text-gray-400 text-sm">
                            <div>Sem cotação</div>
                            <div className="text-xs">Não calculado</div>
                          </div>
                        ) : (
                          <div>
                            <span className={`font-semibold text-lg ${
                              item.divergencia > 0 
                                ? 'text-blue-600' // Peso acima do esperado
                                : item.divergencia < 0 
                                ? 'text-red-600'   // Peso abaixo do esperado
                                : 'text-green-600' // Peso exato
                            }`}>
                              {item.divergencia > 0 ? '+' : ''}{item.divergencia.toFixed(2)} kg
                            </span>
                            {item.divergencia !== 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                {item.divergencia > 0 ? 'Excesso' : 'Falta'}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

            {/* Resumo da conferência para modo somente leitura */}
            {isReadonly && (
              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="text-gray-600 text-sm">📋</div>
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-3">Resumo da Conferência:</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <span className="font-medium">Total de Produtos:</span>
                        <p className="text-gray-900">{itensConferencia.length}</p>
                      </div>
                      <div>
                        <span className="font-medium">Produtos Conformes:</span>
                        <p className="text-green-600 font-semibold">
                          {itensConferencia.filter(item => item.confere).length}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Produtos com Falta:</span>
                        <p className="text-red-600 font-semibold">
                          {itensConferencia.filter(item => !item.confere).length}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">Total Divergências:</span>
                        <p className="font-semibold">
                          {itensConferencia.filter(item => item.divergencia && Math.abs(item.divergencia) > 0).length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Explicação da divergência e regras de preenchimento */}
            {!isReadonly && (
              <div className="mt-6 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <div className="text-amber-600 text-sm">⚠️</div>
                    <div className="text-sm text-amber-700">
                      <p className="font-medium mb-2">Regra de Preenchimento do Campo "Peso (kg)":</p>
                      <div className="space-y-1 text-xs">
                        <p><strong>✓ Produto chegou completo:</strong> Peso é obrigatório</p>
                        <p><strong>✓ Produto chegou parcialmente:</strong> Peso é obrigatório (registra o peso das unidades recebidas)</p>
                        <p><strong>✓ Produto NÃO chegou (faltante = quantidade total):</strong> Peso NÃO é obrigatório</p>
                        <p className="mt-2 text-amber-600"><em>Exemplo: Pedido de 10 unidades, faltaram 10 → não precisa preencher peso</em></p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <div className="text-blue-600 text-sm">📊</div>
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-2">Como funciona o cálculo de Divergência:</p>
                      <div className="space-y-1 text-xs">
                        <p><strong>Fórmula:</strong> Divergência = Peso Recebido - (Quantidade Pedido × Peso por Caixa)</p>
                        <p><strong>Peso por Caixa:</strong> Obtido do fornecedor vencedor na última cotação do produto</p>
                        <p><strong>Cores:</strong> 
                          <span className="text-green-600 font-medium"> Verde = Peso exato</span>, 
                          <span className="text-blue-600 font-medium"> Azul = Excesso</span>, 
                          <span className="text-red-600 font-medium"> Vermelho = Falta</span>
                        </p>
                        <p><strong>"Sem cotação":</strong> Produto ainda não foi cotado ou não há cotações encerradas</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Botões de Ação - Sticky no rodapé */}
            {!isReadonly && (
              <div className="sticky bottom-0 mt-6 pt-4 pb-2 bg-white border-t border-gray-200 z-10 flex justify-end space-x-3">
                {modoEdicao && (
                  <button
                    onClick={() => {
                      // Cancelar edição e voltar para modo readonly
                      iniciarConferencia(pedidoSelecionado, false);
                    }}
                    className="px-6 py-3 rounded-lg font-medium bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors"
                  >
                    Cancelar
                  </button>
                )}
                <button
                  onClick={finalizarConferencia}
                  disabled={!podeFinalizarConferencia() || loading}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    podeFinalizarConferencia() && !loading
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading 
                    ? (modoEdicao ? 'Salvando...' : 'Finalizando...') 
                    : (modoEdicao ? 'Salvar Alterações' : 'Finalizar Conferência')
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Pedidos para Conferência</h3>
        {usuario.tipo_usuario === 'Gerente' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Acesso restrito:</span> Visualizando apenas pedidos da filial <strong>{usuario.filial}</strong>
            </p>
          </div>
        )}
        {(temPermissaoComprador() || usuario.tipo_usuario === 'Administrador') && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-green-800">
              <span className="font-medium">Acesso total:</span> Visualizando pedidos de todas as filiais
            </p>
          </div>
        )}
      </div>
      
      {Object.keys(pedidosPorFilial).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">✅</div>
          <p>Nenhum pedido disponível para conferência.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(pedidosPorFilial).map(([filial, pedidosFilial]) => (
            <div key={filial}>
              {usuario.tipo_usuario !== 'Gerente' && (
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <span className="bg-indigo-100 text-green-800 px-3 py-1 rounded-full text-sm mr-3">
                    {filial}
                  </span>
                  <span className="text-sm text-gray-500">
                    {pedidosFilial.length} pedido(s)
                  </span>
                </h4>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pedidosFilial.map((pedido, index) => (
                  <div key={pedido.id || index} className="bg-gray-50 rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-gray-900 mb-2 text-lg">{pedido.filial || filial || 'Sem filial'}</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Pedido:</span> 
                        <span className="ml-1 px-2 py-1 bg-indigo-100 text-green-800 rounded-full text-xs">
                          #{pedido.numero || `00${index + 1}`}
                        </span>
                      </p>
                      <p><span className="font-medium">Data:</span> {new Date(pedido.criado_em || Date.now()).toLocaleDateString()}</p>
                      <p>
                        <span className="font-medium">Status:</span> 
                        <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                          pedido.status_pedido === 'Conferido' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {pedido.status_pedido === 'Conferido' ? 'Conferido' : 'Pendente'}
                        </span>
                      </p>
                      <p><span className="font-medium">Produtos:</span> {pedido.produtos?.length || 0}</p>
                    </div>
                    <div className="mt-4 space-y-2">
                      <button 
                        onClick={() => iniciarConferencia(pedido)}
                        className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                          pedido.status_pedido === 'Conferido'
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                        data-testid={`conferir-pedido-${pedido.id || index}`}
                      >
                        {pedido.status_pedido === 'Conferido' ? 'Ver Conferência' : 'Conferir Pedido'}
                      </button>
                      
                      {/* Botão de exportação para impressão */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Verificar se já foi conferido
                          const recebimento = recebimentos.find(r => r.pedido_id === pedido.id);
                          
                          if (recebimento) {
                            // Se já foi conferido, usar dados do recebimento
                            const itensParaImprimir = recebimento.itens.map(itemRecebimento => {
                              const produtoOriginal = pedido.produtos?.find(p => 
                                (p.produto_id || p.id) === itemRecebimento.produto_id
                              );
                              return {
                                ...itemRecebimento,
                                quantidade_pedido: produtoOriginal?.quantidade || itemRecebimento.quantidade_pedido || 0,
                                nome_produto: itemRecebimento.nome_produto || produtoOriginal?.nome || 'N/A'
                              };
                            });
                            exportarConferenciaParaImpressao(pedido, itensParaImprimir, true);
                          } else {
                            // Se ainda não foi conferido, usar dados do pedido
                            const itensPedido = (pedido.produtos || []).map(produto => ({
                              produto_id: produto.produto_id || produto.id,
                              nome_produto: produto.nome || produto.nome_produto || 'N/A',
                              quantidade_pedido: produto.quantidade || 0,
                              confere: null,
                              quantidade_faltante: 0,
                              peso: 0,
                              divergencia: 0,
                              peso_por_caixa: 0
                            }));
                            exportarConferenciaParaImpressao(pedido, itensPedido, false);
                          }
                        }}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700 transition-colors"
                      >
                        <span>🖨️</span>
                        <span>Exportar para Impressão</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Bonificacao Tab Component
const BonificacaoTab = ({ bonificacoes = [], produtos, onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showAddForm, setShowAddForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResolverModal, setShowResolverModal] = useState(false);
  const [selectedBonificacao, setSelectedBonificacao] = useState(null);
  const [formData, setFormData] = useState({
    produto_id: '',
    quantidade_perdida: '',
    filial: usuario.tipo_usuario === 'Gerente' ? usuario.filial : '', // Pré-selecionar filial do gerente
    motivo: '',
    arquivo_midia: null
  });
  const [relatorioResolucao, setRelatorioResolucao] = useState('');
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState('');
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchProduto, setSearchProduto] = useState('');

  // Buscar fornecedores ao montar o componente
  useEffect(() => {
    const fetchFornecedores = async () => {
      try {
        const response = await axios.get('/usuarios');
        const fornecedoresList = response.data.filter(u => u.tipo_usuario === 'Fornecedor' && u.ativo);
        setFornecedores(fornecedoresList);
      } catch (error) {
        console.error('Erro ao buscar fornecedores:', error);
      }
    };
    fetchFornecedores();
  }, []);

  // Filtrar bonificações baseado no tipo de usuário
  const bonificacoesFiltradas = (() => {
    // Gerente: apenas da sua filial
    if (usuario.tipo_usuario === 'Gerente') {
      return bonificacoes.filter(b => b.filial === usuario.filial && b.status_bonificacao !== 'Finalizado');
    }
    // Motorista: apenas bonificações aguardando motorista ou aguardando aprovação
    if (usuario.tipo_usuario === 'Motorista') {
      return bonificacoes.filter(b => b.status_bonificacao === 'Aguardando motorista' || b.status_bonificacao === 'Aguardando Aprovação');
    }
    // Comprador/Admin: todas exceto finalizadas
    return bonificacoes.filter(b => b.status_bonificacao !== 'Finalizado');
  })();

  // Filtrar produtos para busca
  const produtosFiltrados = produtos.filter(produto => 
    produto.nome.toLowerCase().includes(searchProduto.toLowerCase())
  );

  // Lista de filiais conforme especificação
  const todasFiliais = [
    'PORTO BELO',
    'SANTANA', 
    'VALE',
    'NOVA AMÉRICA',
    'VIANA',
    'NOVA ROSA',
    'SÃO FRANCISCO'
  ];

  // Filtrar filiais baseado no perfil do usuário
  const filiaisDisponiveis = usuario.tipo_usuario === 'Gerente' 
    ? [usuario.filial] // Gerente só vê sua filial
    : todasFiliais;    // Outros perfis veem todas

  // Funções do formulário
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Função para verificar se formulário está válido
  const isFormularioValido = () => {
    return (
      formData.produto_id &&
      formData.quantidade_perdida &&
      parseFloat(formData.quantidade_perdida) > 0 &&
      formData.filial &&
      formData.motivo &&
      formData.motivo.trim() !== '' &&
      formData.arquivo_midia // Vídeo obrigatório
    );
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar se é vídeo
      if (!file.type.startsWith('video/')) {
        toast({
          title: "Arquivo inválido",
          description: "Somente vídeos são permitidos no campo de Registro fotográfico.",
          variant: "destructive"
        });
        // Limpar input
        event.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          arquivo_midia: e.target.result,
          arquivo_nome: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitBonificacao = async () => {
    // Validações
    if (!formData.produto_id || !formData.quantidade_perdida || !formData.filial || !formData.motivo) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos antes de enviar a bonificação.",
        variant: "destructive"
      });
      return;
    }

    // Validar vídeo obrigatório
    if (!formData.arquivo_midia) {
      toast({
        title: "Vídeo obrigatório",
        description: "É necessário anexar um vídeo no campo de Registro fotográfico.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post('/bonificacoes', formData);
      
      toast({
        title: "Bonificação solicitada!",
        description: "Sua solicitação foi registrada com sucesso"
      });

      // Resetar formulário
      setFormData({
        produto_id: '',
        quantidade_perdida: '',
        filial: usuario.tipo_usuario === 'Gerente' ? usuario.filial : '', // Manter filial do gerente
        motivo: '',
        arquivo_midia: null
      });
      setSearchProduto('');
      setShowAddForm(false);
      onRefresh();

    } catch (error) {
      toast({
        title: "Erro ao solicitar bonificação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizarBonificacao = (bonificacao) => {
    setSelectedBonificacao(bonificacao);
    setShowViewModal(true);
  };

  const handleResolverBonificacao = (bonificacao) => {
    setSelectedBonificacao(bonificacao);
    setRelatorioResolucao('');
    setShowResolverModal(true);
  };

  const handleFinalizarResolucao = async () => {
    if (!relatorioResolucao.trim()) {
      toast({
        title: "Relatório obrigatório",
        description: "Preencha o relatório de resolução",
        variant: "destructive"
      });
      return;
    }

    if (!fornecedorSelecionado) {
      toast({
        title: "Fornecedor obrigatório",
        description: "Selecione um fornecedor para vincular à bonificação",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/bonificacoes/${selectedBonificacao.id}/resolver`, {
        relatorio_resolucao: relatorioResolucao,
        fornecedor_id: fornecedorSelecionado
      });
      
      toast({
        title: "Bonificação resolvida!",
        description: "A resolução foi registrada com sucesso"
      });

      setShowResolverModal(false);
      setSelectedBonificacao(null);
      setRelatorioResolucao('');
      setFornecedorSelecionado('');
      onRefresh();

    } catch (error) {
      toast({
        title: "Erro ao resolver bonificação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para MOTORISTA marcar como recebido
  const handleMarcarRecebido = async (bonificacao) => {
    const confirmed = await showConfirmDialog(
      "Confirmar Recebimento",
      "Confirma que a bonificação foi recebida?"
    );
    
    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.put(`/bonificacoes/${bonificacao.id}/recebido`);
      
      toast({
        title: "Recebimento confirmado!",
        description: "A bonificação está aguardando aprovação do gerente"
      });

      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao confirmar recebimento",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para GERENTE finalizar bonificação
  const handleFinalizarBonificacao = async (bonificacao) => {
    const confirmed = await showConfirmDialog(
      "Finalizar Bonificação",
      "Confirma que a bonificação chegou e deseja finalizá-la?"
    );
    
    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.put(`/bonificacoes/${bonificacao.id}/finalizar`);
      
      toast({
        title: "Bonificação finalizada!",
        description: "O card foi removido da aba"
      });

      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao finalizar bonificação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBonificacao = async (bonificacao) => {
    const confirmed = await showConfirmDialog(
      "Excluir Bonificação",
      "Tem certeza que deseja excluir esta bonificação? Esta ação não poderá ser desfeita."
    );
    
    if (!confirmed) return;

    setLoading(true);
    try {
      await axios.delete(`/bonificacoes/${bonificacao.id}`);
      toast({
        title: "Bonificação excluída!",
        description: "A bonificação foi removida com sucesso"
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir bonificação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Formulário de nova bonificação
  if (showAddForm) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setShowAddForm(false)}
            className="flex items-center space-x-2 text-green-700 hover:text-green-800 mb-4"
          >
            <span>←</span>
            <span>Voltar</span>
          </button>
          <h3 className="text-xl font-semibold text-gray-900">Nova Solicitação de Bonificação</h3>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Campo Produto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Produto *
              </label>
              <input
                type="text"
                value={searchProduto}
                onChange={(e) => setSearchProduto(e.target.value)}
                placeholder="Buscar produto..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 mb-2"
              />
              {searchProduto && (
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg">
                  {produtosFiltrados.map((produto) => (
                    <div
                      key={produto.id}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, produto_id: produto.id }));
                        setSearchProduto(produto.nome);
                      }}
                      className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{produto.nome}</div>
                      <div className="text-sm text-gray-500">{produto.unidade}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Campo Quantidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantidade Perdida *
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.quantidade_perdida}
                onChange={(e) => handleInputChange('quantidade_perdida', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                placeholder="0"
                required
              />
            </div>

            {/* Campo Filial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filial *
              </label>
              <select
                value={formData.filial}
                onChange={(e) => handleInputChange('filial', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                required
              >
                <option value="">
                  {usuario.tipo_usuario === 'Gerente' 
                    ? `Selecionar filial (${usuario.filial})` 
                    : 'Selecionar filial'
                  }
                </option>
                {filiaisDisponiveis.map((filial) => (
                  <option key={filial} value={filial}>{filial}</option>
                ))}
              </select>
              
              {usuario.tipo_usuario === 'Gerente' && (
                <div className="mt-1 text-xs text-gray-500">
                  Como Gerente, você pode solicitar bonificações apenas para sua filial: {usuario.filial}
                </div>
              )}
            </div>

            {/* Campo Registro Fotográfico */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registro Fotográfico (Vídeo) *
              </label>
              <input
                type="file"
                accept="video/*"
                capture="environment"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                required
              />
              {formData.arquivo_midia && (
                <div className="mt-2 text-sm text-green-600 flex items-center space-x-1">
                  <span>✓</span>
                  <span>Vídeo anexado: {formData.arquivo_nome || 'arquivo.mp4'}</span>
                </div>
              )}
              {!formData.arquivo_midia && (
                <div className="mt-1 text-xs text-gray-500">
                  Apenas vídeos são aceitos
                </div>
              )}
            </div>
          </div>

          {/* Campo Motivo */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da Solicitação *
            </label>
            <textarea
              value={formData.motivo}
              onChange={(e) => handleInputChange('motivo', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              placeholder="Descreva o motivo da solicitação de bonificação..."
              required
            />
          </div>

          {/* Botões */}
          <div className="mt-6 flex space-x-4">
            <button
              onClick={handleSubmitBonificacao}
              disabled={loading || !isFormularioValido()}
              className={`px-6 py-2 rounded-lg font-medium ${
                loading || !isFormularioValido()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {loading ? 'Enviando...' : 'Finalizar'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
          
          {/* Mensagem de ajuda quando botão desabilitado */}
          {!isFormularioValido() && !loading && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Preencha todos os campos obrigatórios e anexe um vídeo para habilitar o envio.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          {usuario.tipo_usuario !== 'Motorista' && (
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center space-x-2"
              data-testid="add-bonificacao-button"
            >
              <span>+</span>
              <span>Pedir Bonificação</span>
            </button>
          )}
          
          {usuario.tipo_usuario === 'Gerente' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Filial:</span> {usuario.filial}
              </p>
            </div>
          )}
        </div>
        
        {usuario.tipo_usuario === 'Gerente' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">Atenção:</span> Você pode solicitar bonificação apenas para produtos da filial <strong>{usuario.filial}</strong>
            </p>
          </div>
        )}
        
        {temPermissaoComprador() && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-green-800">
              <span className="font-medium">Acesso total:</span> Visualizando bonificações de todas as filiais
            </p>
          </div>
        )}
      </div>
      
      {bonificacoesFiltradas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🎁</div>
          <p>
            {usuario.tipo_usuario === 'Gerente' 
              ? `Nenhuma bonificação solicitada para ${usuario.filial}.`
              : usuario.tipo_usuario === 'Motorista'
              ? 'Nenhuma bonificação resolvida aguardando recebimento.'
              : 'Nenhuma bonificação registrada ainda.'
            }
          </p>
          {usuario.tipo_usuario !== 'Motorista' && (
            <p className="text-sm mt-2">Clique em "Pedir Bonificação" para criar uma nova solicitação</p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bonificacoesFiltradas.map((bonificacao) => (
              <div key={bonificacao.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-gray-900">
                    {bonificacao.nome_produto}
                  </h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    bonificacao.status_bonificacao === 'Aguardando motorista' 
                      ? 'bg-blue-100 text-blue-800' 
                      : bonificacao.status_bonificacao === 'Aguardando Aprovação'
                      ? 'bg-purple-100 text-purple-800'
                      : bonificacao.status_bonificacao === 'Finalizado'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {bonificacao.status_bonificacao}
                  </span>
                </div>
                
                <div className="text-sm text-gray-600 space-y-1 mb-4">
                  <p><span className="font-medium">Quantidade:</span> {bonificacao.quantidade_perdida}</p>
                  <p><span className="font-medium">Filial:</span> {bonificacao.filial}</p>
                  <p><span className="font-medium">Solicitante:</span> {bonificacao.solicitante_nome}</p>
                  <p><span className="font-medium">Data:</span> {new Date(bonificacao.criado_em).toLocaleDateString()}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {bonificacao.motivo.length > 50 
                      ? `${bonificacao.motivo.substring(0, 50)}...`
                      : bonificacao.motivo
                    }
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleVisualizarBonificacao(bonificacao)}
                    className="flex-1 px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  >
                    Visualizar
                  </button>
                  
                  {temPermissaoComprador() && bonificacao.status_bonificacao === 'Pendente' && (
                    <button 
                      onClick={() => handleResolverBonificacao(bonificacao)}
                      className="flex-1 px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                    >
                      Resolver
                    </button>
                  )}
                  
                  {usuario.tipo_usuario === 'Motorista' && bonificacao.status_bonificacao === 'Aguardando motorista' && (
                    <button 
                      onClick={() => handleMarcarRecebido(bonificacao)}
                      className="flex-1 px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200"
                    >
                      Recebido
                    </button>
                  )}
                  
                  {usuario.tipo_usuario === 'Gerente' && bonificacao.status_bonificacao === 'Aguardando Aprovação' && (
                    <button 
                      onClick={() => handleFinalizarBonificacao(bonificacao)}
                      className="flex-1 px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 font-bold"
                    >
                      CHEGOU!
                    </button>
                  )}
                  
                  {temPermissaoComprador() && (
                    <button 
                      onClick={() => handleDeleteBonificacao(bonificacao)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                    >
                      Excluir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {showViewModal && selectedBonificacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowViewModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Detalhes da Bonificação
                </h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="font-medium text-gray-700">Produto:</span>
                  <p className="text-gray-900">{selectedBonificacao.nome_produto}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Quantidade:</span>
                  <p className="text-gray-900">{selectedBonificacao.quantidade_perdida}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Filial:</span>
                  <p className="text-gray-900">{selectedBonificacao.filial}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Status:</span>
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                    selectedBonificacao.status_bonificacao === 'Aguardando motorista' 
                      ? 'bg-blue-100 text-blue-800' 
                      : selectedBonificacao.status_bonificacao === 'Aguardando Aprovação'
                      ? 'bg-purple-100 text-purple-800'
                      : selectedBonificacao.status_bonificacao === 'Finalizado'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedBonificacao.status_bonificacao}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Solicitante:</span>
                  <p className="text-gray-900">{selectedBonificacao.solicitante_nome}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Data:</span>
                  <p className="text-gray-900">{new Date(selectedBonificacao.criado_em).toLocaleString()}</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="font-medium text-gray-700">Motivo:</span>
                <p className="text-gray-900 mt-1 bg-gray-50 p-3 rounded-lg">
                  {selectedBonificacao.motivo}
                </p>
              </div>

              {selectedBonificacao.arquivo_midia && (
                <div className="mb-6">
                  <span className="font-medium text-gray-700">Registro Fotográfico:</span>
                  <div className="mt-2">
                    <img 
                      src={selectedBonificacao.arquivo_midia} 
                      alt="Registro da bonificação"
                      className="max-w-full h-auto max-h-64 rounded-lg border"
                    />
                  </div>
                </div>
              )}

              {(selectedBonificacao.status_bonificacao === 'Aguardando motorista' || selectedBonificacao.status_bonificacao === 'Aguardando Aprovação' || selectedBonificacao.status_bonificacao === 'Finalizado') && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800 mb-2">Resolução</h4>
                  {selectedBonificacao.fornecedor_nome && (
                    <div className="mb-3">
                      <span className="font-medium text-green-700">Fornecedor Vinculado:</span>
                      <p className="text-green-900">{selectedBonificacao.fornecedor_nome}</p>
                    </div>
                  )}
                  <div className="text-sm text-green-700 space-y-1">
                    <p><strong>Resolvido por:</strong> {selectedBonificacao.resolvido_por}</p>
                    <p><strong>Data:</strong> {new Date(selectedBonificacao.resolvido_em).toLocaleString()}</p>
                    <p className="mt-2"><strong>Relatório:</strong></p>
                    <p className="bg-white p-3 rounded border">
                      {selectedBonificacao.relatorio_resolucao}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Resolver */}
      {showResolverModal && selectedBonificacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowResolverModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Resolver Bonificação
                </h3>
                <button
                  onClick={() => setShowResolverModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-2">{selectedBonificacao.nome_produto}</h4>
                <div className="text-sm text-gray-600 grid grid-cols-2 gap-2">
                  <p><strong>Quantidade:</strong> {selectedBonificacao.quantidade_perdida}</p>
                  <p><strong>Filial:</strong> {selectedBonificacao.filial}</p>
                  <p><strong>Solicitante:</strong> {selectedBonificacao.solicitante_nome}</p>
                  <p><strong>Data:</strong> {new Date(selectedBonificacao.criado_em).toLocaleDateString()}</p>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Motivo:</strong> {selectedBonificacao.motivo}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fornecedor Vinculado *
                </label>
                <select
                  value={fornecedorSelecionado}
                  onChange={(e) => setFornecedorSelecionado(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Selecione um fornecedor</option>
                  {fornecedores.map(fornecedor => (
                    <option key={fornecedor.id} value={fornecedor.id}>
                      {fornecedor.nome_completo || fornecedor.nome || fornecedor.login}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecione o fornecedor responsável por esta bonificação
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relatório de Resolução *
                </label>
                <textarea
                  value={relatorioResolucao}
                  onChange={(e) => setRelatorioResolucao(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Descreva como a bonificação foi resolvida, as ações tomadas e o desfecho..."
                  required
                />
              </div>

              <div className="mt-6 flex space-x-4">
                <button
                  onClick={handleFinalizarResolucao}
                  disabled={loading || !relatorioResolucao.trim()}
                  className={`flex-1 px-6 py-2 rounded-lg font-medium ${
                    loading || !relatorioResolucao.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {loading ? 'Finalizando...' : 'Finalizar Resolução'}
                </button>
                <button
                  onClick={() => setShowResolverModal(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const savedUsuario = localStorage.getItem('usuario');
    
    if (token && savedUsuario) {
      const parsedUsuario = JSON.parse(savedUsuario);
      setUsuario(parsedUsuario);
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={
              usuario ? (
                <Dashboard usuario={usuario} onLogout={() => setUsuario(null)} />
              ) : (
                <Login onLogin={setUsuario} />
              )
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
